import { useState, useEffect, useRef, createContext, useContext } from "react";
import AboutPage from "./AboutPage";
import ContactPage from "./ContactPage";
import NewsPage from "./NewsPage";
import NewsDetailPage from "./NewsDetailPage";
import ProductsPage from "./ProductsPage";
import { NEWS_ARTICLES } from "./newsData";

const COLORS = {
  navy: "#003366",
  navyDark: "#001f44",
  navyMid: "#004080",
  red: "#cc0000",
  redLight: "#e60000",
  greyPage: "#f0f2f5",
  greyCard: "#e5e8ed",
  greyMid: "#c8cdd6",
  greyDark: "#555e6d",
  white: "#ffffff",
  textDark: "#1a2233",
  textMid: "#3d4a5c",
};

const IMAGES = {
  logoWhite: "https://arcuchicnc.com/wp-content/uploads/2023/07/Untitled-6.png",
  logoFooter: "https://arcuchicnc.com/wp-content/uploads/2023/07/Untitled-6.png",
  controllerUnit: "https://arcuchicnc.com/wp-content/uploads/2023/09/1-1.png",
  factory: "https://arcuchicnc.com/wp-content/uploads/2023/07/sdfds.jpg",
  inject: "https://arcuchicnc.com/wp-content/uploads/2025/08/INJECT.jpg",
  wire: "https://arcuchicnc.com/wp-content/uploads/2025/08/WIRE.jpg",
  stamping: "https://arcuchicnc.com/wp-content/uploads/2025/08/Stamping-Forming-Solution-1.jpg",
  hydraulic: "https://arcuchicnc.com/wp-content/uploads/2025/08/HYDRAULIC.jpg",
  blogServo: "https://arcuchicnc.com/wp-content/uploads/2024/06/blog-arcuchi-1024x576.jpg",
  blogPlc: "https://arcuchicnc.com/wp-content/uploads/2024/05/How-to-choose-best-PLC-controller-of-plastic-injection-molding-machine-1-1024x538.png",
  blogHydraulic: "https://arcuchicnc.com/wp-content/uploads/2024/04/Let-us-show-yu-the-way.-1-1024x538.png",
  iconWarranty: "https://arcuchicnc.com/wp-content/uploads/2023/07/Layer-4.png",
  icon247: "https://arcuchicnc.com/wp-content/uploads/2023/07/Layer-6.png",
  iconUpgrade: "https://arcuchicnc.com/wp-content/uploads/2023/07/Layer-21-copy.png",
  iconMaint: "https://arcuchicnc.com/wp-content/uploads/2023/07/Layer-21-copy-2.png",
  partner1: "https://arcuchicnc.com/wp-content/uploads/2023/08/1-300x63.png",
  partner2: "https://arcuchicnc.com/wp-content/uploads/2023/08/logo-2-300x62.png",
  partner3: "https://arcuchicnc.com/wp-content/uploads/2023/08/3-300x90.png",
  partner4: "https://arcuchicnc.com/wp-content/uploads/2023/08/4-300x83.png",
  partnerPJ: "https://arcuchicnc.com/wp-content/uploads/2023/08/POWERJET-LOGO-300x146-1.webp",
  partner6: "https://arcuchicnc.com/wp-content/uploads/2023/08/B96BEE87-DEBD-4cc7-820B-59ACFFF7E749.png",
};

// ─── Language Context ────────────────────────────────────────────────────────

const LangContext = createContext("en");
const useLang = () => useContext(LangContext);

const LANGUAGES = [
  { code: "en", label: "English",    flag: "🇬🇧" },
  { code: "es", label: "Español",    flag: "🇪🇸" },
  { code: "ko", label: "한국어",      flag: "🇰🇷" },
  { code: "ja", label: "日本語",      flag: "🇯🇵" },
  { code: "th", label: "ภาษาไทย",   flag: "🇹🇭" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "tr", label: "Türkçe",     flag: "🇹🇷" },
  { code: "ru", label: "Русский",    flag: "🇷🇺" },
  { code: "id", label: "Indonesia",  flag: "🇮🇩" },
  { code: "pt", label: "Português",  flag: "🇵🇹" },
];

const TRANSLATIONS = {
  en: {
    nav: { links: ["Home","Solutions","Products","About","Services","News","Contact"], quote: "Get Quote" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Est. 2007", title: ["Precision","Industrial Control","Solutions"], accent: 1, desc: "Advanced IMM controllers, servo systems & frequency converters engineered for injection molding, wire forming, hydraulic presses and stamping machines worldwide." },
        { tag: "Wire Forming Solutions", title: ["High-Speed","CNC Wire","Forming"], accent: 2, desc: "Sub-millimeter repeatability with servo-driven precision for wire benders and spring coilers across high-volume production environments." },
        { tag: "Stamping & Forming", title: ["Smart","Stamping","Control"], accent: 0, desc: "Tonnage monitoring, stroke control and die protection for high-volume stamping lines with automated defect detection and real-time analytics." },
        { tag: "Hydraulic Press Systems", title: ["Advanced","Hydraulic","Press Control"], accent: 2, desc: "Proportional valve control, synchronized multi-cylinder motion and pressure profiling for forming and forging lines worldwide." },
      ],
      exploreBtn: "Explore Products", watchBtn: "Watch Demo",
    },
    stats: [["18+","Years of Excellence"],["50+","Global Offices"],["200+","Patents & IP"],["30K+","Happy Clients"]],
    marquee: ["IMM Controller","Servo System","Frequency Converter","Wire Forming","Injection Molding","Hydraulic Press","Stamping Forming","24/7 Global Support"],
    solutions: {
      label: "Our Solutions", title: "Industry", accent: "Applications",
      sub: "Advanced controllers and servos tailored for injection molding, wire forming, stamping and hydraulic press forming machines.",
      cards: [
        { num: "01 / Solution", title: "Injection Molding", desc: "Precise mold temperature control, clamping force management and real-time cycle analytics for plastic injection machines." },
        { num: "02 / Solution", title: "Wire Forming", desc: "High-speed CNC wire benders and spring coilers with sub-millimeter repeatability and servo-driven precision." },
        { num: "03 / Solution", title: "Stamping Forming", desc: "Tonnage monitoring, stroke control and die protection for high-volume stamping lines with automated defect detection." },
        { num: "04 / Solution", title: "Hydraulic Press", desc: "Proportional valve control, synchronized multi-cylinder motion and pressure profiling for forming and forging lines." },
      ],
      viewDetails: "View Details",
    },
    about: {
      label: "About Arcuchi", heading: "Futuristic", accent: "Solutions", headingSuffix: "for Manufacturing",
      p1: "Arcuchi, founded in 2007, is a global leader providing cutting-edge industrial equipment solutions. With a focus on high-quality, technologically advanced controllers and servos, we specialize in injection molding, wire forming, hydraulic presses and stamping machines.",
      p2: "Proudly driven by in-house R&D and innovation, we empower manufacturers worldwide to enhance production capabilities and achieve sustainable success.",
      stats: [{ value: "17+", label: "Years Experience" },{ value: "50+", label: "Countries Served" },{ value: "1000+", label: "Machines Installed" }],
      btn: "More About Us →",
    },
    services: {
      label: "Our Service", title: "After-Sales", accent: "Support",
      sub: "Arcuchi has the most reliable after-sales support. Our service system guarantees uninterrupted machine performance.",
      items: [
        { title: "Warranty", desc: "1-year manufacturer warranty on all products with rapid spare parts dispatch worldwide." },
        { title: "24×7 Service", desc: "Round-the-clock engineering support via phone, video call and remote diagnostics." },
        { title: "Machine Upgrade", desc: "Retrofit solutions to modernize existing machines with new control architecture and IoT connectivity." },
        { title: "Maintenance AMC", desc: "Annual maintenance contracts with predictive diagnostics and priority response SLAs." },
      ],
    },
    testimonials: {
      label: "Client Feedback", title: "What Clients", accent: "Say",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "Mexico", text: "IMM Controller by Arcuchi boosted our efficiency. Easy-to-use interface and remote monitoring improved our production immensely." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Japan", text: "Arcuchi's servo system controller revolutionized our operations. Enhanced control, reduced downtime significantly across all lines." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Brazil", text: "Precision meets innovation with Arcuchi's injection molding PLC Controller. Improved quality, training made easy for our entire team." },
      ],
    },
    news: {
      label: "News & Events", title: "Latest", accent: "Updates", viewAll: "View All Articles →",
      readArticle: "Read Article", readMore: "Read More",
      articles: [
        { tag: "Technology", date: "June 5, 2024", title: "Features of a Servo System", excerpt: "Precision and efficiency are paramount in industrial applications such as robotics, CNC machinery, and injection molding. Discover how modern servo systems are revolutionizing the manufacturing floor." },
        { tag: "Control Systems", date: "May 20, 2024", title: "How to Choose the Best PLC Controller", excerpt: "Choosing the right controller is crucial for optimizing your injection molding machine's performance and reliability." },
        { tag: "Hydraulics", date: "April 18, 2024", title: "What Is a Hydraulic AC Servo System?", excerpt: "A hydraulic AC servo system uses hydraulic fluid to transmit power and control motion, offering high performance." },
      ],
    },
    partners: "Trusted by Leading Manufacturers Worldwide",
    contact: {
      label: "Get in Touch", title: "Contact", accent: "Us",
      desc: "Whether you need a standard solution or a fully customized system, our engineers are ready to help you optimize your production line.",
      infoLabels: { phone: "Phone", email: "Email", address: "Address", support: "Support" },
      supportVal: "24 Hours / 7 Days a Week",
      form: { nameLbl: "Your Name", namePlh: "Full name", emailLbl: "Email Address", emailPlh: "name@company.com", productLbl: "Product Interest", productPlh: "Select a product", products: ["IMM Controller","Servo System","Frequency Converter","Machine Retrofit / Upgrade"], msgLbl: "Message", msgPlh: "Describe your application or requirements", sendBtn: "Send Message →" },
    },
    footer: {
      desc: "Enhance your production efficiency with advanced controllers and servos for various industrial equipment worldwide.",
      cols: [
        { title: "Products", links: ["IMM Controller","Servo System","Frequency Converter","Retrofit Kits"] },
        { title: "Company", links: ["About Us","News & Events","Blogs","Careers"] },
        { title: "Support", links: ["Contact Us","Technical Docs","AMC Program","WhatsApp Chat"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. All rights reserved.",
    },
  },
  es: {
    nav: { links: ["Inicio","Soluciones","Productos","Acerca","Servicios","Noticias","Contacto"], quote: "Cotizar" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Est. 2007", title: ["Precisión","Control","Industrial"], accent: 1, desc: "Controladores IMM avanzados, servosistemas y convertidores de frecuencia para moldeo por inyección, formado de alambre, prensas hidráulicas y máquinas de estampado en todo el mundo." },
        { tag: "Soluciones de Formado de Alambre", title: ["Alta","Velocidad CNC","Formado"], accent: 2, desc: "Repetibilidad submilimétrica con precisión servo para dobladores de alambre y bobinadores en entornos de producción de alto volumen." },
        { tag: "Estampado y Conformado", title: ["Control","Inteligente de","Estampado"], accent: 0, desc: "Monitoreo de tonelaje, control de carrera y protección de troqueles para líneas de estampado de alto volumen con detección automática de defectos." },
        { tag: "Sistemas de Prensa Hidráulica", title: ["Control","Avanzado de","Prensa Hidráulica"], accent: 2, desc: "Control de válvula proporcional, movimiento multi-cilindro sincronizado y perfilado de presión para líneas de conformado y forja." },
      ],
      exploreBtn: "Ver Productos", watchBtn: "Ver Demo",
    },
    stats: [["18+","Años de Excelencia"],["50+","Oficinas Globales"],["200+","Patentes e IP"],["30K+","Clientes Felices"]],
    marquee: ["Controlador IMM","Sistema Servo","Convertidor de Frecuencia","Formado de Alambre","Moldeo por Inyección","Prensa Hidráulica","Estampado","Soporte Global 24/7"],
    solutions: {
      label: "Nuestras Soluciones", title: "Aplicaciones", accent: "Industriales",
      sub: "Controladores y servos avanzados para moldeo por inyección, formado de alambre, estampado y prensas hidráulicas.",
      cards: [
        { num: "01 / Solución", title: "Moldeo por Inyección", desc: "Control preciso de temperatura, gestión de fuerza de cierre y análisis de ciclos en tiempo real para máquinas de inyección." },
        { num: "02 / Solución", title: "Formado de Alambre", desc: "Dobladores CNC de alta velocidad con repetibilidad submilimétrica y precisión servo." },
        { num: "03 / Solución", title: "Estampado", desc: "Monitoreo de tonelaje, control de carrera y protección de troqueles con detección automática de defectos." },
        { num: "04 / Solución", title: "Prensa Hidráulica", desc: "Control de válvula proporcional, movimiento multi-cilindro sincronizado y perfilado de presión." },
      ],
      viewDetails: "Ver Detalles",
    },
    about: {
      label: "Sobre Arcuchi", heading: "Soluciones", accent: "Futuristas", headingSuffix: "para la Manufactura",
      p1: "Arcuchi, fundada en 2007, es líder mundial en soluciones avanzadas de equipos industriales. Especializados en controladores y servos de alta calidad para moldeo por inyección, formado de alambre, prensas hidráulicas y estampado.",
      p2: "Impulsados por I+D interno, empoderamos a fabricantes de todo el mundo para mejorar su capacidad productiva y alcanzar el éxito sostenible.",
      stats: [{ value: "17+", label: "Años de Experiencia" },{ value: "50+", label: "Países Atendidos" },{ value: "1000+", label: "Máquinas Instaladas" }],
      btn: "Más Sobre Nosotros →",
    },
    services: {
      label: "Nuestro Servicio", title: "Soporte", accent: "Postventa",
      sub: "Arcuchi tiene el soporte postventa más confiable. Nuestro sistema garantiza un rendimiento ininterrumpido de la máquina.",
      items: [
        { title: "Garantía", desc: "Garantía de 1 año del fabricante con envío rápido de repuestos en todo el mundo." },
        { title: "Servicio 24×7", desc: "Soporte de ingeniería continuo por teléfono, videollamada y diagnóstico remoto." },
        { title: "Actualización de Máquina", desc: "Soluciones de modernización con nueva arquitectura de control y conectividad IoT." },
        { title: "Mantenimiento AMC", desc: "Contratos de mantenimiento anual con diagnósticos predictivos y SLAs prioritarios." },
      ],
    },
    testimonials: {
      label: "Opiniones de Clientes", title: "Lo que dicen", accent: "nuestros clientes",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "México", text: "El controlador IMM de Arcuchi mejoró nuestra eficiencia. La interfaz fácil y el monitoreo remoto transformaron nuestra producción." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Japón", text: "El sistema servo de Arcuchi revolucionó nuestras operaciones. Mayor control y menor tiempo de inactividad en todas las líneas." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Brasil", text: "Precisión e innovación con el controlador PLC de Arcuchi. Mejor calidad y capacitación sencilla para todo el equipo." },
      ],
    },
    news: {
      label: "Noticias y Eventos", title: "Últimas", accent: "Actualizaciones", viewAll: "Ver Todos los Artículos →",
      readArticle: "Leer Artículo", readMore: "Leer Más",
      articles: [
        { tag: "Tecnología", date: "5 Junio 2024", title: "Características de un Sistema Servo", excerpt: "La precisión y eficiencia son fundamentales en robótica, maquinaria CNC y moldeo por inyección. Descubra cómo los modernos sistemas servo están revolucionando la manufactura." },
        { tag: "Sistemas de Control", date: "20 Mayo 2024", title: "Cómo Elegir el Mejor Controlador PLC", excerpt: "Elegir el controlador correcto es crucial para optimizar el rendimiento de su máquina de moldeo por inyección." },
        { tag: "Hidráulica", date: "18 Abril 2024", title: "¿Qué es un Sistema Servo AC Hidráulico?", excerpt: "Un sistema servo AC hidráulico usa fluido hidráulico para transmitir potencia y controlar el movimiento con alto rendimiento." },
      ],
    },
    partners: "De confianza para fabricantes líderes en todo el mundo",
    contact: {
      label: "Contáctenos", title: "Contáct", accent: "enos",
      desc: "Ya sea que necesite una solución estándar o un sistema personalizado, nuestros ingenieros están listos para optimizar su línea de producción.",
      infoLabels: { phone: "Teléfono", email: "Correo", address: "Dirección", support: "Soporte" },
      supportVal: "24 Horas / 7 Días a la Semana",
      form: { nameLbl: "Su Nombre", namePlh: "Nombre completo", emailLbl: "Correo Electrónico", emailPlh: "nombre@empresa.com", productLbl: "Producto de Interés", productPlh: "Seleccione un producto", products: ["Controlador IMM","Sistema Servo","Convertidor de Frecuencia","Modernización de Máquina"], msgLbl: "Mensaje", msgPlh: "Describa su aplicación o requisitos", sendBtn: "Enviar Mensaje →" },
    },
    footer: {
      desc: "Mejore la eficiencia de su producción con controladores y servos avanzados para equipos industriales en todo el mundo.",
      cols: [
        { title: "Productos", links: ["Controlador IMM","Sistema Servo","Convertidor de Frecuencia","Kits de Modernización"] },
        { title: "Empresa", links: ["Sobre Nosotros","Noticias y Eventos","Blog","Empleo"] },
        { title: "Soporte", links: ["Contáctenos","Documentación Técnica","Programa AMC","Chat WhatsApp"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. Todos los derechos reservados.",
    },
  },
  ko: {
    nav: { links: ["홈","솔루션","제품","회사소개","서비스","뉴스","문의"], quote: "견적 요청" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — 2007년 설립", title: ["정밀","산업용 제어","솔루션"], accent: 1, desc: "사출 성형, 와이어 포밍, 유압 프레스 및 스탬핑 기계용으로 설계된 첨단 IMM 컨트롤러, 서보 시스템 및 인버터." },
        { tag: "와이어 포밍 솔루션", title: ["고속","CNC 와이어","포밍"], accent: 2, desc: "와이어 벤더 및 스프링 코일러를 위한 서브밀리미터 정밀도의 서보 구동 솔루션." },
        { tag: "스탬핑 및 포밍", title: ["스마트","스탬핑","제어"], accent: 0, desc: "자동 불량 감지와 실시간 분석이 포함된 고용량 스탬핑 라인용 톤수 모니터링 및 스트로크 제어." },
        { tag: "유압 프레스 시스템", title: ["첨단","유압","프레스 제어"], accent: 2, desc: "비례 밸브 제어, 동기화된 다중 실린더 운동 및 포밍·단조 라인용 압력 프로파일링." },
      ],
      exploreBtn: "제품 보기", watchBtn: "데모 보기",
    },
    stats: [["18+","우수한 연혁"],["50+","글로벌 사무소"],["200+","특허 및 IP"],["30K+","만족한 고객"]],
    marquee: ["IMM 컨트롤러","서보 시스템","인버터","와이어 포밍","사출 성형","유압 프레스","스탬핑","24/7 글로벌 지원"],
    solutions: {
      label: "솔루션", title: "산업", accent: "응용 분야",
      sub: "사출 성형, 와이어 포밍, 스탬핑 및 유압 프레스에 맞춘 첨단 컨트롤러 및 서보.",
      cards: [
        { num: "01 / 솔루션", title: "사출 성형", desc: "플라스틱 사출 기계를 위한 정밀한 금형 온도 제어, 클램핑 힘 관리 및 실시간 사이클 분석." },
        { num: "02 / 솔루션", title: "와이어 포밍", desc: "서브밀리미터 반복 정밀도의 고속 CNC 와이어 벤더 및 스프링 코일러." },
        { num: "03 / 솔루션", title: "스탬핑 포밍", desc: "자동 불량 감지가 포함된 고용량 스탬핑 라인용 톤수 모니터링 및 스트로크 제어." },
        { num: "04 / 솔루션", title: "유압 프레스", desc: "포밍·단조 라인용 비례 밸브 제어 및 동기화된 다중 실린더 운동." },
      ],
      viewDetails: "자세히 보기",
    },
    about: {
      label: "Arcuchi 소개", heading: "미래형", accent: "솔루션", headingSuffix: "제조업을 위한",
      p1: "2007년 설립된 Arcuchi는 첨단 산업 장비 솔루션을 제공하는 글로벌 리더입니다. 고품질 컨트롤러와 서보를 통해 사출 성형, 와이어 포밍, 유압 프레스, 스탬핑 기계를 전문으로 합니다.",
      p2: "자체 R&D와 혁신을 통해 전 세계 제조업체가 생산 능력을 향상시키고 지속 가능한 성공을 달성할 수 있도록 지원합니다.",
      stats: [{ value: "17+", label: "년 경력" },{ value: "50+", label: "서비스 국가" },{ value: "1000+", label: "설치된 기계" }],
      btn: "더 알아보기 →",
    },
    services: {
      label: "서비스", title: "사후", accent: "지원",
      sub: "Arcuchi는 가장 신뢰할 수 있는 사후 지원을 제공합니다. 서비스 시스템이 기계의 지속적인 성능을 보장합니다.",
      items: [
        { title: "보증", desc: "모든 제품에 대한 1년 제조사 보증과 신속한 부품 배송." },
        { title: "24×7 서비스", desc: "전화, 화상 통화 및 원격 진단을 통한 24시간 엔지니어링 지원." },
        { title: "기계 업그레이드", desc: "새로운 제어 아키텍처 및 IoT 연결로 기존 기계를 현대화하는 레트로핏 솔루션." },
        { title: "유지보수 AMC", desc: "예측 진단 및 우선 응답 SLA가 포함된 연간 유지보수 계약." },
      ],
    },
    testimonials: {
      label: "고객 후기", title: "고객들의", accent: "의견",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "멕시코", text: "Arcuchi의 IMM 컨트롤러로 효율성이 높아졌습니다. 직관적인 인터페이스와 원격 모니터링으로 생산성이 크게 향상되었습니다." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "일본", text: "Arcuchi의 서보 시스템 컨트롤러가 운영을 혁신했습니다. 향상된 제어와 현저히 감소된 다운타임." },
        { initials: "CR", name: "Carlos Rodrigues", country: "브라질", text: "Arcuchi의 PLC 컨트롤러는 정밀함과 혁신의 만남입니다. 품질 향상과 용이한 교육으로 팀 전체가 만족합니다." },
      ],
    },
    news: {
      label: "뉴스 & 이벤트", title: "최신", accent: "업데이트", viewAll: "모든 기사 보기 →",
      readArticle: "기사 읽기", readMore: "더 읽기",
      articles: [
        { tag: "기술", date: "2024년 6월 5일", title: "서보 시스템의 특징", excerpt: "로봇공학, CNC 기계 및 사출 성형 등 산업 응용에서 정밀도와 효율성이 중요합니다. 현대 서보 시스템이 제조 현장을 어떻게 혁신하는지 알아보세요." },
        { tag: "제어 시스템", date: "2024년 5월 20일", title: "최적의 PLC 컨트롤러 선택 방법", excerpt: "올바른 컨트롤러 선택은 사출 성형기의 성능과 신뢰성을 최적화하는 데 중요합니다." },
        { tag: "유압", date: "2024년 4월 18일", title: "유압 AC 서보 시스템이란?", excerpt: "유압 AC 서보 시스템은 유압 오일을 사용해 동력을 전달하고 운동을 제어하여 높은 성능을 제공합니다." },
      ],
    },
    partners: "전 세계 주요 제조업체가 신뢰하는 파트너",
    contact: {
      label: "문의하기", title: "문의", accent: "하기",
      desc: "표준 솔루션이나 완전 맞춤형 시스템이 필요하든 당사 엔지니어들이 생산 라인 최적화를 도와드립니다.",
      infoLabels: { phone: "전화", email: "이메일", address: "주소", support: "지원" },
      supportVal: "연중무휴 24시간",
      form: { nameLbl: "성명", namePlh: "이름을 입력하세요", emailLbl: "이메일 주소", emailPlh: "name@company.com", productLbl: "관심 제품", productPlh: "제품 선택", products: ["IMM 컨트롤러","서보 시스템","인버터","기계 레트로핏"], msgLbl: "메시지", msgPlh: "응용 분야 또는 요구 사항을 설명해 주세요", sendBtn: "메시지 보내기 →" },
    },
    footer: {
      desc: "전 세계 다양한 산업 장비를 위한 첨단 컨트롤러와 서보로 생산 효율성을 향상시키세요.",
      cols: [
        { title: "제품", links: ["IMM 컨트롤러","서보 시스템","인버터","레트로핏 키트"] },
        { title: "회사", links: ["회사 소개","뉴스 & 이벤트","블로그","채용"] },
        { title: "지원", links: ["문의하기","기술 문서","AMC 프로그램","WhatsApp 채팅"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. All rights reserved.",
    },
  },
  ja: {
    nav: { links: ["ホーム","ソリューション","製品","会社概要","サービス","ニュース","お問い合わせ"], quote: "見積依頼" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — 2007年設立", title: ["精密","産業用制御","ソリューション"], accent: 1, desc: "射出成形、ワイヤーフォーミング、油圧プレス、スタンピング機械向けの高度なIMMコントローラー、サーボシステム、インバーター。" },
        { tag: "ワイヤーフォーミングソリューション", title: ["高速","CNCワイヤー","フォーミング"], accent: 2, desc: "ワイヤーベンダーやスプリングコイラーのためのサブミリメートル精度のサーボ駆動ソリューション。" },
        { tag: "スタンピング＆フォーミング", title: ["スマート","スタンピング","制御"], accent: 0, desc: "自動欠陥検出とリアルタイム分析を備えた大量生産スタンピングラインのトン数監視とストローク制御。" },
        { tag: "油圧プレスシステム", title: ["高度な","油圧","プレス制御"], accent: 2, desc: "フォーミング・鍛造ラインのための比例弁制御、同期マルチシリンダー動作、圧力プロファイリング。" },
      ],
      exploreBtn: "製品を見る", watchBtn: "デモを見る",
    },
    stats: [["18+","年の実績"],["50+","グローバル拠点"],["200+","特許・IP"],["30K+","満足した顧客"]],
    marquee: ["IMMコントローラー","サーボシステム","インバーター","ワイヤーフォーミング","射出成形","油圧プレス","スタンピング","24/7グローバルサポート"],
    solutions: {
      label: "ソリューション", title: "産業用", accent: "アプリケーション",
      sub: "射出成形、ワイヤーフォーミング、スタンピング、油圧プレス向けの高度なコントローラーとサーボ。",
      cards: [
        { num: "01 / ソリューション", title: "射出成形", desc: "プラスチック射出機械のための精密な金型温度制御、クランプ力管理、リアルタイムサイクル分析。" },
        { num: "02 / ソリューション", title: "ワイヤーフォーミング", desc: "サブミリメートル精度の高速CNCワイヤーベンダーとスプリングコイラー。" },
        { num: "03 / ソリューション", title: "スタンピングフォーミング", desc: "自動欠陥検出を備えた大量スタンピングラインのトン数監視とストローク制御。" },
        { num: "04 / ソリューション", title: "油圧プレス", desc: "フォーミング・鍛造ラインのための比例弁制御と同期マルチシリンダー動作。" },
      ],
      viewDetails: "詳細を見る",
    },
    about: {
      label: "Arcuchiについて", heading: "未来型", accent: "ソリューション", headingSuffix: "製造業のための",
      p1: "2007年設立のArcuchiは、最先端の産業機器ソリューションを提供するグローバルリーダーです。射出成形、ワイヤーフォーミング、油圧プレス、スタンピング機械向けの高品質コントローラーとサーボを専門とします。",
      p2: "自社R&Dとイノベーションによって、世界中のメーカーの生産能力向上と持続可能な成功を支援します。",
      stats: [{ value: "17+", label: "年の経験" },{ value: "50+", label: "対応国数" },{ value: "1000+", label: "設置台数" }],
      btn: "詳しく見る →",
    },
    services: {
      label: "サービス", title: "アフター", accent: "サポート",
      sub: "Arcuchiは最も信頼性の高いアフターサポートを提供します。サービスシステムがマシンの継続的な性能を保証します。",
      items: [
        { title: "保証", desc: "全製品に1年間のメーカー保証と迅速なスペアパーツ配送。" },
        { title: "24×7サービス", desc: "電話、ビデオ通話、リモート診断によるエンジニアリングサポート。" },
        { title: "マシンアップグレード", desc: "新しい制御アーキテクチャとIoT接続による既存機械の近代化ソリューション。" },
        { title: "保守AMC", desc: "予測診断と優先応答SLAを含む年間保守契約。" },
      ],
    },
    testimonials: {
      label: "顧客の声", title: "お客様の", accent: "ご意見",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "メキシコ", text: "ArcuchiのIMMコントローラーで効率が向上しました。直感的なインターフェースとリモート監視が生産を大幅に改善しました。" },
        { initials: "HT", name: "Hiroshi Tanaka", country: "日本", text: "Arcuchiのサーボシステムコントローラーが業務を革新しました。制御が強化され、ダウンタイムが大幅に減少しました。" },
        { initials: "CR", name: "Carlos Rodrigues", country: "ブラジル", text: "ArcuchiのPLCコントローラーは精密さと革新の融合です。品質向上と容易な研修でチーム全体が満足しています。" },
      ],
    },
    news: {
      label: "ニュース＆イベント", title: "最新", accent: "情報", viewAll: "全記事を見る →",
      readArticle: "記事を読む", readMore: "続きを読む",
      articles: [
        { tag: "テクノロジー", date: "2024年6月5日", title: "サーボシステムの特徴", excerpt: "ロボット工学、CNC機械、射出成形などの産業応用で精度と効率が重要です。現代のサーボシステムが製造現場をどのように革新しているかをご覧ください。" },
        { tag: "制御システム", date: "2024年5月20日", title: "最適なPLCコントローラーの選び方", excerpt: "適切なコントローラーの選択は、射出成形機の性能と信頼性を最適化するために重要です。" },
        { tag: "油圧", date: "2024年4月18日", title: "油圧ACサーボシステムとは？", excerpt: "油圧ACサーボシステムは油圧オイルを使用して動力を伝達し、運動を制御することで高性能を提供します。" },
      ],
    },
    partners: "世界中の主要メーカーに信頼されています",
    contact: {
      label: "お問い合わせ", title: "お問い合わせ", accent: "はこちら",
      desc: "標準ソリューションでも完全カスタムシステムでも、当社のエンジニアが生産ラインの最適化をサポートします。",
      infoLabels: { phone: "電話", email: "メール", address: "住所", support: "サポート" },
      supportVal: "24時間 / 週7日",
      form: { nameLbl: "お名前", namePlh: "フルネーム", emailLbl: "メールアドレス", emailPlh: "name@company.com", productLbl: "ご関心の製品", productPlh: "製品を選択", products: ["IMMコントローラー","サーボシステム","インバーター","マシン改造・アップグレード"], msgLbl: "メッセージ", msgPlh: "用途や要件をご説明ください", sendBtn: "メッセージを送る →" },
    },
    footer: {
      desc: "世界中のさまざまな産業機器向けの高度なコントローラーとサーボで生産効率を向上させます。",
      cols: [
        { title: "製品", links: ["IMMコントローラー","サーボシステム","インバーター","レトロフィットキット"] },
        { title: "会社", links: ["会社概要","ニュース＆イベント","ブログ","採用情報"] },
        { title: "サポート", links: ["お問い合わせ","技術文書","AMCプログラム","WhatsAppチャット"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. All rights reserved.",
    },
  },
  th: {
    nav: { links: ["หน้าหลัก","โซลูชัน","ผลิตภัณฑ์","เกี่ยวกับ","บริการ","ข่าวสาร","ติดต่อ"], quote: "ขอใบเสนอราคา" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — ก่อตั้ง 2007", title: ["ความแม่นยำ","การควบคุม","อุตสาหกรรม"], accent: 1, desc: "คอนโทรลเลอร์ IMM ขั้นสูง ระบบเซอร์โว และอินเวอร์เตอร์สำหรับเครื่องฉีดพลาสติก การขึ้นรูปลวด เครื่องอัดไฮดรอลิก และเครื่องปั๊ม" },
        { tag: "โซลูชันการขึ้นรูปลวด", title: ["ความเร็วสูง","CNC ลวด","การขึ้นรูป"], accent: 2, desc: "ความแม่นยำระดับต่ำกว่ามิลลิเมตรสำหรับเครื่องดัดลวดและเครื่องพันสปริงในสภาพแวดล้อมการผลิตปริมาณสูง" },
        { tag: "การปั๊มและการขึ้นรูป", title: ["การควบคุม","การปั๊ม","อัจฉริยะ"], accent: 0, desc: "การตรวจสอบแรงตัน การควบคุมจังหวะ และการป้องกันแม่พิมพ์สำหรับสายการปั๊มปริมาณสูงพร้อมการตรวจจับข้อบกพร่องอัตโนมัติ" },
        { tag: "ระบบเครื่องอัดไฮดรอลิก", title: ["การควบคุม","เครื่องอัด","ไฮดรอลิกขั้นสูง"], accent: 2, desc: "การควบคุมวาล์วสัดส่วน การเคลื่อนที่แบบซิงโครไนซ์หลายกระบอก และการสร้างโปรไฟล์แรงดันสำหรับสายการขึ้นรูปและการตีขึ้นรูป" },
      ],
      exploreBtn: "ดูผลิตภัณฑ์", watchBtn: "ดูการสาธิต",
    },
    stats: [["18+","ปีแห่งความเป็นเลิศ"],["50+","สำนักงานทั่วโลก"],["200+","สิทธิบัตร & IP"],["30K+","ลูกค้าที่พึงพอใจ"]],
    marquee: ["คอนโทรลเลอร์ IMM","ระบบเซอร์โว","อินเวอร์เตอร์","การขึ้นรูปลวด","การฉีดพลาสติก","เครื่องอัดไฮดรอลิก","การปั๊ม","การสนับสนุน 24/7"],
    solutions: {
      label: "โซลูชันของเรา", title: "การประยุกต์ใช้", accent: "ในอุตสาหกรรม",
      sub: "คอนโทรลเลอร์และเซอร์โวขั้นสูงสำหรับเครื่องฉีดพลาสติก การขึ้นรูปลวด การปั๊ม และเครื่องอัดไฮดรอลิก",
      cards: [
        { num: "01 / โซลูชัน", title: "การฉีดพลาสติก", desc: "การควบคุมอุณหภูมิแม่พิมพ์ที่แม่นยำ การจัดการแรงจับยึด และการวิเคราะห์วงจรแบบเรียลไทม์" },
        { num: "02 / โซลูชัน", title: "การขึ้นรูปลวด", desc: "เครื่องดัดลวด CNC ความเร็วสูงและเครื่องพันสปริงพร้อมความแม่นยำระดับต่ำกว่ามิลลิเมตร" },
        { num: "03 / โซลูชัน", title: "การปั๊มขึ้นรูป", desc: "การตรวจสอบแรงตัน การควบคุมจังหวะ และการป้องกันแม่พิมพ์พร้อมการตรวจจับข้อบกพร่องอัตโนมัติ" },
        { num: "04 / โซลูชัน", title: "เครื่องอัดไฮดรอลิก", desc: "การควบคุมวาล์วสัดส่วน การเคลื่อนที่แบบซิงโครไนซ์หลายกระบอก และการสร้างโปรไฟล์แรงดัน" },
      ],
      viewDetails: "ดูรายละเอียด",
    },
    about: {
      label: "เกี่ยวกับ Arcuchi", heading: "โซลูชัน", accent: "แห่งอนาคต", headingSuffix: "สำหรับการผลิต",
      p1: "Arcuchi ก่อตั้งในปี 2007 เป็นผู้นำระดับโลกในการจัดหาโซลูชันอุปกรณ์อุตสาหกรรมล้ำสมัย เชี่ยวชาญด้านคอนโทรลเลอร์และเซอร์โวคุณภาพสูงสำหรับการฉีดพลาสติก การขึ้นรูปลวด เครื่องอัดไฮดรอลิก และเครื่องปั๊ม",
      p2: "ขับเคลื่อนด้วย R&D ภายในและนวัตกรรม เราช่วยให้ผู้ผลิตทั่วโลกพัฒนาขีดความสามารถการผลิตและบรรลุความสำเร็จอย่างยั่งยืน",
      stats: [{ value: "17+", label: "ปีแห่งประสบการณ์" },{ value: "50+", label: "ประเทศที่ให้บริการ" },{ value: "1000+", label: "เครื่องจักรที่ติดตั้ง" }],
      btn: "เรียนรู้เพิ่มเติม →",
    },
    services: {
      label: "บริการของเรา", title: "การสนับสนุน", accent: "หลังการขาย",
      sub: "Arcuchi มีการสนับสนุนหลังการขายที่เชื่อถือได้มากที่สุด ระบบบริการของเรารับประกันประสิทธิภาพของเครื่องจักรอย่างต่อเนื่อง",
      items: [
        { title: "การรับประกัน", desc: "การรับประกันจากผู้ผลิต 1 ปีสำหรับผลิตภัณฑ์ทั้งหมดพร้อมจัดส่งอะไหล่รวดเร็วทั่วโลก" },
        { title: "บริการ 24×7", desc: "การสนับสนุนทางวิศวกรรมตลอด 24 ชั่วโมงผ่านโทรศัพท์ วิดีโอคอล และการวินิจฉัยระยะไกล" },
        { title: "การอัปเกรดเครื่องจักร", desc: "โซลูชันรีโทรฟิตเพื่อปรับปรุงเครื่องจักรที่มีอยู่ด้วยสถาปัตยกรรมการควบคุมใหม่และการเชื่อมต่อ IoT" },
        { title: "AMC บำรุงรักษา", desc: "สัญญาบำรุงรักษาประจำปีพร้อมการวินิจฉัยเชิงคาดการณ์และ SLA การตอบสนองแบบลำดับความสำคัญ" },
      ],
    },
    testimonials: {
      label: "ความคิดเห็นลูกค้า", title: "สิ่งที่ลูกค้า", accent: "พูดถึง",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "เม็กซิโก", text: "คอนโทรลเลอร์ IMM ของ Arcuchi ช่วยเพิ่มประสิทธิภาพของเรา อินเทอร์เฟซที่ใช้งานง่ายและการตรวจสอบระยะไกลช่วยปรับปรุงการผลิตของเราอย่างมาก" },
        { initials: "HT", name: "Hiroshi Tanaka", country: "ญี่ปุ่น", text: "คอนโทรลเลอร์ระบบเซอร์โวของ Arcuchi ปฏิวัติการดำเนินงานของเรา การควบคุมที่ดีขึ้นและลดเวลาหยุดทำงานอย่างมีนัยสำคัญ" },
        { initials: "CR", name: "Carlos Rodrigues", country: "บราซิล", text: "ความแม่นยำผสมกับนวัตกรรมด้วยคอนโทรลเลอร์ PLC ของ Arcuchi คุณภาพที่ดีขึ้นและการฝึกอบรมที่ง่ายสำหรับทีมทั้งหมด" },
      ],
    },
    news: {
      label: "ข่าวสารและกิจกรรม", title: "อัปเดต", accent: "ล่าสุด", viewAll: "ดูบทความทั้งหมด →",
      readArticle: "อ่านบทความ", readMore: "อ่านเพิ่มเติม",
      articles: [
        { tag: "เทคโนโลยี", date: "5 มิถุนายน 2024", title: "คุณสมบัติของระบบเซอร์โว", excerpt: "ความแม่นยำและประสิทธิภาพมีความสำคัญในการประยุกต์ใช้งานอุตสาหกรรม เช่น หุ่นยนต์ เครื่องจักร CNC และการฉีดพลาสติก" },
        { tag: "ระบบควบคุม", date: "20 พฤษภาคม 2024", title: "วิธีเลือกคอนโทรลเลอร์ PLC ที่ดีที่สุด", excerpt: "การเลือกคอนโทรลเลอร์ที่ถูกต้องมีความสำคัญในการเพิ่มประสิทธิภาพของเครื่องฉีดพลาสติก" },
        { tag: "ไฮดรอลิก", date: "18 เมษายน 2024", title: "ระบบเซอร์โว AC ไฮดรอลิกคืออะไร?", excerpt: "ระบบเซอร์โว AC ไฮดรอลิกใช้น้ำมันไฮดรอลิกในการส่งพลังงานและควบคุมการเคลื่อนที่ เพื่อประสิทธิภาพสูง" },
      ],
    },
    partners: "ได้รับความไว้วางใจจากผู้ผลิตชั้นนำทั่วโลก",
    contact: {
      label: "ติดต่อเรา", title: "ติดต่อ", accent: "เรา",
      desc: "ไม่ว่าคุณต้องการโซลูชันมาตรฐานหรือระบบที่กำหนดเอง วิศวกรของเราพร้อมช่วยเพิ่มประสิทธิภาพสายการผลิตของคุณ",
      infoLabels: { phone: "โทรศัพท์", email: "อีเมล", address: "ที่อยู่", support: "การสนับสนุน" },
      supportVal: "24 ชั่วโมง / 7 วันต่อสัปดาห์",
      form: { nameLbl: "ชื่อของคุณ", namePlh: "ชื่อเต็ม", emailLbl: "ที่อยู่อีเมล", emailPlh: "name@company.com", productLbl: "ผลิตภัณฑ์ที่สนใจ", productPlh: "เลือกผลิตภัณฑ์", products: ["คอนโทรลเลอร์ IMM","ระบบเซอร์โว","อินเวอร์เตอร์","การรีโทรฟิตเครื่องจักร"], msgLbl: "ข้อความ", msgPlh: "อธิบายการใช้งานหรือความต้องการของคุณ", sendBtn: "ส่งข้อความ →" },
    },
    footer: {
      desc: "เพิ่มประสิทธิภาพการผลิตของคุณด้วยคอนโทรลเลอร์และเซอร์โวขั้นสูงสำหรับอุปกรณ์อุตสาหกรรมต่างๆ ทั่วโลก",
      cols: [
        { title: "ผลิตภัณฑ์", links: ["คอนโทรลเลอร์ IMM","ระบบเซอร์โว","อินเวอร์เตอร์","ชุดรีโทรฟิต"] },
        { title: "บริษัท", links: ["เกี่ยวกับเรา","ข่าวสารและกิจกรรม","บล็อก","ร่วมงานกับเรา"] },
        { title: "การสนับสนุน", links: ["ติดต่อเรา","เอกสารทางเทคนิค","โปรแกรม AMC","แชท WhatsApp"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. สงวนลิขสิทธิ์",
    },
  },
  vi: {
    nav: { links: ["Trang Chủ","Giải Pháp","Sản Phẩm","Giới Thiệu","Dịch Vụ","Tin Tức","Liên Hệ"], quote: "Báo Giá" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Thành lập 2007", title: ["Giải Pháp","Điều Khiển","Công Nghiệp"], accent: 1, desc: "Bộ điều khiển IMM, hệ thống servo và biến tần tiên tiến cho máy ép nhựa, uốn dây, máy ép thủy lực và máy dập trên toàn thế giới." },
        { tag: "Giải Pháp Tạo Hình Dây", title: ["Tốc Độ Cao","Uốn Dây","CNC"], accent: 2, desc: "Độ lặp lại dưới mili-mét với độ chính xác servo cho máy uốn dây và cuộn lò xo trong môi trường sản xuất khối lượng lớn." },
        { tag: "Dập & Tạo Hình", title: ["Điều Khiển","Dập","Thông Minh"], accent: 0, desc: "Giám sát tải trọng, kiểm soát hành trình và bảo vệ khuôn cho dây chuyền dập khối lượng cao với phát hiện lỗi tự động." },
        { tag: "Hệ Thống Máy Ép Thủy Lực", title: ["Điều Khiển","Máy Ép Thủy Lực","Nâng Cao"], accent: 2, desc: "Điều khiển van tỷ lệ, chuyển động xi-lanh đa trục đồng bộ và lập hồ sơ áp suất cho dây chuyền tạo hình và rèn." },
      ],
      exploreBtn: "Xem Sản Phẩm", watchBtn: "Xem Demo",
    },
    stats: [["18+","Năm Xuất Sắc"],["50+","Văn Phòng Toàn Cầu"],["200+","Bằng Sáng Chế & IP"],["30K+","Khách Hàng Hài Lòng"]],
    marquee: ["Bộ Điều Khiển IMM","Hệ Thống Servo","Biến Tần","Uốn Dây","Ép Nhựa","Máy Ép Thủy Lực","Dập","Hỗ Trợ 24/7"],
    solutions: {
      label: "Giải Pháp", title: "Ứng Dụng", accent: "Công Nghiệp",
      sub: "Bộ điều khiển và servo tiên tiến cho máy ép nhựa, uốn dây, dập và máy ép thủy lực.",
      cards: [
        { num: "01 / Giải Pháp", title: "Ép Phun Nhựa", desc: "Kiểm soát nhiệt độ khuôn chính xác, quản lý lực kẹp và phân tích chu kỳ thời gian thực cho máy ép nhựa." },
        { num: "02 / Giải Pháp", title: "Uốn Dây", desc: "Máy uốn dây CNC tốc độ cao và cuộn lò xo với độ lặp lại dưới mili-mét và độ chính xác servo." },
        { num: "03 / Giải Pháp", title: "Dập Tạo Hình", desc: "Giám sát tải trọng, kiểm soát hành trình và bảo vệ khuôn với phát hiện lỗi tự động." },
        { num: "04 / Giải Pháp", title: "Máy Ép Thủy Lực", desc: "Điều khiển van tỷ lệ, chuyển động xi-lanh đa trục đồng bộ và lập hồ sơ áp suất." },
      ],
      viewDetails: "Xem Chi Tiết",
    },
    about: {
      label: "Về Arcuchi", heading: "Giải Pháp", accent: "Tương Lai", headingSuffix: "Cho Sản Xuất",
      p1: "Arcuchi, thành lập năm 2007, là công ty dẫn đầu toàn cầu cung cấp giải pháp thiết bị công nghiệp tiên tiến. Chuyên về bộ điều khiển và servo chất lượng cao cho máy ép nhựa, uốn dây, máy ép thủy lực và dập.",
      p2: "Được thúc đẩy bởi R&D nội bộ và sự đổi mới, chúng tôi giúp các nhà sản xuất trên toàn thế giới nâng cao năng lực sản xuất và đạt được thành công bền vững.",
      stats: [{ value: "17+", label: "Năm Kinh Nghiệm" },{ value: "50+", label: "Quốc Gia Phục Vụ" },{ value: "1000+", label: "Máy Đã Lắp Đặt" }],
      btn: "Tìm Hiểu Thêm →",
    },
    services: {
      label: "Dịch Vụ", title: "Hỗ Trợ", accent: "Sau Bán Hàng",
      sub: "Arcuchi có dịch vụ hỗ trợ sau bán hàng đáng tin cậy nhất. Hệ thống dịch vụ của chúng tôi đảm bảo hiệu suất máy liên tục.",
      items: [
        { title: "Bảo Hành", desc: "Bảo hành 1 năm từ nhà sản xuất cho tất cả sản phẩm với giao phụ tùng nhanh toàn cầu." },
        { title: "Dịch Vụ 24×7", desc: "Hỗ trợ kỹ thuật 24/7 qua điện thoại, video call và chẩn đoán từ xa." },
        { title: "Nâng Cấp Máy", desc: "Giải pháp retrofit để hiện đại hóa máy hiện có với kiến trúc điều khiển mới và kết nối IoT." },
        { title: "Bảo Dưỡng AMC", desc: "Hợp đồng bảo dưỡng hàng năm với chẩn đoán dự đoán và SLA ưu tiên." },
      ],
    },
    testimonials: {
      label: "Phản Hồi Khách Hàng", title: "Khách Hàng", accent: "Nói Gì",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "Mexico", text: "Bộ điều khiển IMM của Arcuchi tăng cường hiệu quả của chúng tôi. Giao diện dễ sử dụng và giám sát từ xa cải thiện sản xuất đáng kể." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Nhật Bản", text: "Bộ điều khiển servo của Arcuchi cách mạng hóa hoạt động của chúng tôi. Kiểm soát tốt hơn, giảm thời gian ngừng hoạt động đáng kể." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Brazil", text: "Độ chính xác gặp đổi mới với bộ điều khiển PLC của Arcuchi. Chất lượng cải thiện và đào tạo dễ dàng cho toàn bộ nhóm." },
      ],
    },
    news: {
      label: "Tin Tức & Sự Kiện", title: "Cập Nhật", accent: "Mới Nhất", viewAll: "Xem Tất Cả Bài Viết →",
      readArticle: "Đọc Bài Viết", readMore: "Đọc Thêm",
      articles: [
        { tag: "Công Nghệ", date: "5 Tháng 6 2024", title: "Tính Năng Của Hệ Thống Servo", excerpt: "Độ chính xác và hiệu quả là tối quan trọng trong các ứng dụng công nghiệp như robot, máy CNC và ép nhựa. Khám phá cách hệ thống servo hiện đại đang cách mạng hóa sản xuất." },
        { tag: "Hệ Thống Điều Khiển", date: "20 Tháng 5 2024", title: "Cách Chọn Bộ Điều Khiển PLC Tốt Nhất", excerpt: "Chọn đúng bộ điều khiển là điều quan trọng để tối ưu hóa hiệu suất máy ép nhựa của bạn." },
        { tag: "Thủy Lực", date: "18 Tháng 4 2024", title: "Hệ Thống Servo AC Thủy Lực Là Gì?", excerpt: "Hệ thống servo AC thủy lực sử dụng dầu thủy lực để truyền năng lượng và kiểm soát chuyển động với hiệu suất cao." },
      ],
    },
    partners: "Được tin cậy bởi các nhà sản xuất hàng đầu trên toàn thế giới",
    contact: {
      label: "Liên Hệ", title: "Liên", accent: "Hệ",
      desc: "Dù bạn cần giải pháp tiêu chuẩn hay hệ thống tùy chỉnh hoàn toàn, kỹ sư của chúng tôi sẵn sàng giúp tối ưu hóa dây chuyền sản xuất.",
      infoLabels: { phone: "Điện Thoại", email: "Email", address: "Địa Chỉ", support: "Hỗ Trợ" },
      supportVal: "24 Giờ / 7 Ngày Một Tuần",
      form: { nameLbl: "Tên Của Bạn", namePlh: "Họ và tên", emailLbl: "Địa Chỉ Email", emailPlh: "name@company.com", productLbl: "Sản Phẩm Quan Tâm", productPlh: "Chọn sản phẩm", products: ["Bộ Điều Khiển IMM","Hệ Thống Servo","Biến Tần","Nâng Cấp Máy"], msgLbl: "Tin Nhắn", msgPlh: "Mô tả ứng dụng hoặc yêu cầu của bạn", sendBtn: "Gửi Tin Nhắn →" },
    },
    footer: {
      desc: "Nâng cao hiệu quả sản xuất với bộ điều khiển và servo tiên tiến cho các thiết bị công nghiệp trên toàn thế giới.",
      cols: [
        { title: "Sản Phẩm", links: ["Bộ Điều Khiển IMM","Hệ Thống Servo","Biến Tần","Bộ Retrofit"] },
        { title: "Công Ty", links: ["Về Chúng Tôi","Tin Tức & Sự Kiện","Blog","Tuyển Dụng"] },
        { title: "Hỗ Trợ", links: ["Liên Hệ","Tài Liệu Kỹ Thuật","Chương Trình AMC","Chat WhatsApp"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. Bảo lưu mọi quyền.",
    },
  },
  tr: {
    nav: { links: ["Ana Sayfa","Çözümler","Ürünler","Hakkımızda","Hizmetler","Haberler","İletişim"], quote: "Teklif Al" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Kur. 2007", title: ["Hassas","Endüstriyel Kontrol","Çözümleri"], accent: 1, desc: "Enjeksiyon kalıplama, tel şekillendirme, hidrolik presler ve damgalama makineleri için gelişmiş IMM kontrolörler, servo sistemler ve frekans konvertörleri." },
        { tag: "Tel Şekillendirme Çözümleri", title: ["Yüksek Hızlı","CNC Tel","Şekillendirme"], accent: 2, desc: "Tel bükücüler ve yay sarma makineleri için servo tahrikli milimetrenin altı hassasiyetle tekrarlanabilirlik." },
        { tag: "Damgalama ve Şekillendirme", title: ["Akıllı","Damgalama","Kontrolü"], accent: 0, desc: "Otomatik hata tespiti ile yüksek hacimli damgalama hatları için ton izleme, kontur kontrolü ve kalıp koruması." },
        { tag: "Hidrolik Pres Sistemleri", title: ["Gelişmiş","Hidrolik","Pres Kontrolü"], accent: 2, desc: "Şekillendirme ve dövme hatları için orantısal valf kontrolü, senkronize çok silindirli hareket ve basınç profilleme." },
      ],
      exploreBtn: "Ürünleri Keşfet", watchBtn: "Demo İzle",
    },
    stats: [["18+","Yıllık Mükemmellik"],["50+","Küresel Ofis"],["200+","Patent & IP"],["30K+","Mutlu Müşteri"]],
    marquee: ["IMM Kontrolör","Servo Sistem","Frekans Konvertörü","Tel Şekillendirme","Enjeksiyon Kalıplama","Hidrolik Pres","Damgalama","7/24 Küresel Destek"],
    solutions: {
      label: "Çözümlerimiz", title: "Endüstriyel", accent: "Uygulamalar",
      sub: "Enjeksiyon kalıplama, tel şekillendirme, damgalama ve hidrolik pres makineleri için gelişmiş kontrolörler ve servolar.",
      cards: [
        { num: "01 / Çözüm", title: "Enjeksiyon Kalıplama", desc: "Plastik enjeksiyon makineleri için hassas kalıp sıcaklık kontrolü, kenetleme kuvveti yönetimi ve gerçek zamanlı döngü analitiği." },
        { num: "02 / Çözüm", title: "Tel Şekillendirme", desc: "Milimetrenin altı tekrarlanabilirlik ve servo tahrikli hassasiyetle yüksek hızlı CNC tel bükücüler ve yay sarma makineleri." },
        { num: "03 / Çözüm", title: "Damgalama Şekillendirme", desc: "Otomatik hata tespiti ile yüksek hacimli damgalama hatları için ton izleme, kontur kontrolü ve kalıp koruması." },
        { num: "04 / Çözüm", title: "Hidrolik Pres", desc: "Şekillendirme ve dövme hatları için orantısal valf kontrolü ve senkronize çok silindirli hareket." },
      ],
      viewDetails: "Detayları Gör",
    },
    about: {
      label: "Arcuchi Hakkında", heading: "Geleceğin", accent: "Çözümleri", headingSuffix: "Üretim İçin",
      p1: "2007 yılında kurulan Arcuchi, son teknoloji endüstriyel ekipman çözümleri sağlayan küresel bir liderdir. Enjeksiyon kalıplama, tel şekillendirme, hidrolik presler ve damgalama makineleri için yüksek kaliteli kontrolörler ve servolar konusunda uzmanlaşmıştır.",
      p2: "Şirket içi Ar-Ge ve inovasyonla desteklenen Arcuchi, dünya genelindeki üreticilerin üretim kapasitelerini artırmasına ve sürdürülebilir başarı elde etmesine yardımcı olur.",
      stats: [{ value: "17+", label: "Yıllık Deneyim" },{ value: "50+", label: "Hizmet Ülkesi" },{ value: "1000+", label: "Kurulu Makine" }],
      btn: "Daha Fazla Bilgi →",
    },
    services: {
      label: "Hizmetlerimiz", title: "Satış Sonrası", accent: "Destek",
      sub: "Arcuchi en güvenilir satış sonrası desteğe sahiptir. Hizmet sistemimiz makinenin kesintisiz performansını garanti eder.",
      items: [
        { title: "Garanti", desc: "Tüm ürünler için 1 yıl üretici garantisi ve hızlı yedek parça teslimatı." },
        { title: "24×7 Hizmet", desc: "Telefon, görüntülü arama ve uzaktan teşhis yoluyla kesintisiz mühendislik desteği." },
        { title: "Makine Yükseltme", desc: "Mevcut makineleri yeni kontrol mimarisi ve IoT bağlantısıyla modernize etmek için retrofit çözümleri." },
        { title: "Bakım AMC", desc: "Öngörücü teşhis ve öncelikli SLA ile yıllık bakım sözleşmeleri." },
      ],
    },
    testimonials: {
      label: "Müşteri Görüşleri", title: "Müşteriler", accent: "Ne Diyor",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "Meksika", text: "Arcuchi'nin IMM Kontrolörü verimliliğimizi artırdı. Kullanımı kolay arayüz ve uzaktan izleme üretimimizi önemli ölçüde geliştirdi." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Japonya", text: "Arcuchi'nin servo sistem kontrolörü operasyonlarımızı devrimleştirdi. Gelişmiş kontrol ve tüm hatlarda önemli ölçüde azalan duruş süresi." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Brezilya", text: "Arcuchi'nin PLC Kontrolörü ile hassasiyet ve inovasyon buluşuyor. Kalite arttı, ekibimiz için eğitim kolaylaştı." },
      ],
    },
    news: {
      label: "Haberler & Etkinlikler", title: "Son", accent: "Güncellemeler", viewAll: "Tüm Makaleleri Gör →",
      readArticle: "Makaleyi Oku", readMore: "Devamını Oku",
      articles: [
        { tag: "Teknoloji", date: "5 Haziran 2024", title: "Servo Sisteminin Özellikleri", excerpt: "Robotik, CNC makineleri ve enjeksiyon kalıplama gibi endüstriyel uygulamalarda hassasiyet ve verimlilik çok önemlidir. Modern servo sistemlerin üretimi nasıl dönüştürdüğünü keşfedin." },
        { tag: "Kontrol Sistemleri", date: "20 Mayıs 2024", title: "En İyi PLC Kontrolörü Nasıl Seçilir", excerpt: "Doğru kontrolörü seçmek, enjeksiyon kalıplama makinenizin performansını ve güvenilirliğini optimize etmek için çok önemlidir." },
        { tag: "Hidrolik", date: "18 Nisan 2024", title: "Hidrolik AC Servo Sistemi Nedir?", excerpt: "Hidrolik AC servo sistemi, güç iletimi ve hareket kontrolü için hidrolik yağ kullanarak yüksek performans sağlar." },
      ],
    },
    partners: "Dünya genelinde önde gelen üreticiler tarafından güvenilen marka",
    contact: {
      label: "İletişime Geçin", title: "Bize", accent: "Ulaşın",
      desc: "Standart bir çözüme veya tam özel sisteme ihtiyaç duyun, mühendislerimiz üretim hattınızı optimize etmeye hazır.",
      infoLabels: { phone: "Telefon", email: "E-posta", address: "Adres", support: "Destek" },
      supportVal: "Haftanın 7 Günü 24 Saat",
      form: { nameLbl: "Adınız", namePlh: "Tam adınız", emailLbl: "E-posta Adresi", emailPlh: "isim@firma.com", productLbl: "İlgilendiğiniz Ürün", productPlh: "Ürün seçin", products: ["IMM Kontrolör","Servo Sistem","Frekans Konvertörü","Makine Modernizasyonu"], msgLbl: "Mesaj", msgPlh: "Uygulamanızı veya gereksinimlerinizi açıklayın", sendBtn: "Mesaj Gönder →" },
    },
    footer: {
      desc: "Dünya genelindeki çeşitli endüstriyel ekipmanlar için gelişmiş kontrolörler ve servolarla üretim verimliliğinizi artırın.",
      cols: [
        { title: "Ürünler", links: ["IMM Kontrolör","Servo Sistem","Frekans Konvertörü","Retrofit Kitleri"] },
        { title: "Şirket", links: ["Hakkımızda","Haberler & Etkinlikler","Blog","Kariyer"] },
        { title: "Destek", links: ["Bize Ulaşın","Teknik Belgeler","AMC Programı","WhatsApp Sohbet"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. Tüm hakları saklıdır.",
    },
  },
  ru: {
    nav: { links: ["Главная","Решения","Продукты","О нас","Услуги","Новости","Контакты"], quote: "Запрос цены" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Осн. 2007", title: ["Точные","Промышленные","Решения"], accent: 1, desc: "Передовые контроллеры IMM, серводвигатели и преобразователи частоты для литья под давлением, формовки проволоки, гидравлических прессов и штамповочного оборудования." },
        { tag: "Решения для Формовки Проволоки", title: ["Высокоскоростная","CNC Формовка","Проволоки"], accent: 2, desc: "Точность ниже миллиметра с сервоприводом для проволокогибочных машин и пружинонавивочных станков." },
        { tag: "Штамповка и Формовка", title: ["Умное","Управление","Штамповкой"], accent: 0, desc: "Мониторинг тоннажа, контроль хода и защита штампа для высокообъёмных штамповочных линий с автоматическим обнаружением дефектов." },
        { tag: "Гидравлические Прессовые Системы", title: ["Управление","Гидравлическим","Прессом"], accent: 2, desc: "Управление пропорциональным клапаном, синхронизированное многоцилиндровое движение и профилирование давления для формовочных и ковочных линий." },
      ],
      exploreBtn: "Смотреть Продукты", watchBtn: "Смотреть Демо",
    },
    stats: [["18+","Лет Совершенства"],["50+","Глобальных Офисов"],["200+","Патентов и IP"],["30К+","Довольных Клиентов"]],
    marquee: ["Контроллер IMM","Серводвигатель","Преобразователь Частоты","Формовка Проволоки","Литьё под Давлением","Гидравлический Пресс","Штамповка","Поддержка 24/7"],
    solutions: {
      label: "Наши Решения", title: "Промышленные", accent: "Приложения",
      sub: "Передовые контроллеры и сервоприводы для литья под давлением, формовки проволоки, штамповки и гидравлических прессов.",
      cards: [
        { num: "01 / Решение", title: "Литьё под Давлением", desc: "Точный контроль температуры формы, управление усилием смыкания и аналитика цикла для машин литья под давлением." },
        { num: "02 / Решение", title: "Формовка Проволоки", desc: "Высокоскоростные CNC проволокогибочные машины с точностью ниже миллиметра и сервоприводом." },
        { num: "03 / Решение", title: "Штамповка", desc: "Мониторинг тоннажа, контроль хода и защита штампа с автоматическим обнаружением дефектов." },
        { num: "04 / Решение", title: "Гидравлический Пресс", desc: "Управление пропорциональным клапаном, синхронизированное многоцилиндровое движение и профилирование давления." },
      ],
      viewDetails: "Подробнее",
    },
    about: {
      label: "Об Arcuchi", heading: "Решения", accent: "Будущего", headingSuffix: "для Производства",
      p1: "Arcuchi, основанная в 2007 году, является мировым лидером в области передовых решений для промышленного оборудования. Специализируется на высококачественных контроллерах и сервоприводах для литья под давлением, формовки проволоки, гидравлических прессов и штамповочного оборудования.",
      p2: "Опираясь на внутренние НИОКР и инновации, мы помогаем производителям по всему миру повышать производственный потенциал и добиваться устойчивого успеха.",
      stats: [{ value: "17+", label: "Лет Опыта" },{ value: "50+", label: "Стран Охвата" },{ value: "1000+", label: "Установленных Машин" }],
      btn: "Подробнее о Нас →",
    },
    services: {
      label: "Наши Услуги", title: "Послепродажная", accent: "Поддержка",
      sub: "Arcuchi обеспечивает наиболее надёжную послепродажную поддержку. Наша система сервиса гарантирует бесперебойную работу оборудования.",
      items: [
        { title: "Гарантия", desc: "Гарантия производителя 1 год на все продукты с быстрой доставкой запчастей по всему миру." },
        { title: "Сервис 24×7", desc: "Круглосуточная инженерная поддержка по телефону, видеозвонку и удалённой диагностике." },
        { title: "Модернизация", desc: "Решения по модернизации существующих машин с новой архитектурой управления и IoT-подключением." },
        { title: "АМС Обслуживание", desc: "Ежегодные контракты на обслуживание с прогнозной диагностикой и приоритетными SLA." },
      ],
    },
    testimonials: {
      label: "Отзывы Клиентов", title: "Что говорят", accent: "Клиенты",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "Мексика", text: "Контроллер IMM от Arcuchi повысил нашу эффективность. Простой интерфейс и удалённый мониторинг значительно улучшили производство." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Япония", text: "Сервоконтроллер Arcuchi революционизировал наши операции. Улучшенное управление, значительно сниженные простои на всех линиях." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Бразилия", text: "Точность в сочетании с инновациями — ПЛК-контроллер Arcuchi. Улучшилось качество, обучение стало проще для всей команды." },
      ],
    },
    news: {
      label: "Новости и События", title: "Последние", accent: "Обновления", viewAll: "Все Статьи →",
      readArticle: "Читать Статью", readMore: "Читать Далее",
      articles: [
        { tag: "Технологии", date: "5 Июня 2024", title: "Особенности Серводвигателя", excerpt: "Точность и эффективность имеют первостепенное значение в промышленных приложениях, таких как робототехника, CNC и литьё под давлением. Узнайте, как современные системы преобразуют производство." },
        { tag: "Системы Управления", date: "20 Мая 2024", title: "Как Выбрать Лучший ПЛК-Контроллер", excerpt: "Выбор правильного контроллера имеет решающее значение для оптимизации производительности машины литья под давлением." },
        { tag: "Гидравлика", date: "18 Апреля 2024", title: "Что такое Гидравлическая AC Серводвигатель?", excerpt: "Гидравлический AC серводвигатель использует гидравлическую жидкость для передачи мощности и управления движением с высокой производительностью." },
      ],
    },
    partners: "Доверие ведущих производителей по всему миру",
    contact: {
      label: "Связаться с Нами", title: "Контакт", accent: "ы",
      desc: "Нужно ли вам стандартное решение или полностью настраиваемая система — наши инженеры готовы помочь оптимизировать производственную линию.",
      infoLabels: { phone: "Телефон", email: "Email", address: "Адрес", support: "Поддержка" },
      supportVal: "24 Часа / 7 Дней в Неделю",
      form: { nameLbl: "Ваше Имя", namePlh: "Полное имя", emailLbl: "Email Адрес", emailPlh: "name@company.com", productLbl: "Интересующий Продукт", productPlh: "Выберите продукт", products: ["Контроллер IMM","Серводвигатель","Преобразователь Частоты","Модернизация Машин"], msgLbl: "Сообщение", msgPlh: "Опишите ваше применение или требования", sendBtn: "Отправить Сообщение →" },
    },
    footer: {
      desc: "Повысьте эффективность производства с помощью передовых контроллеров и сервоприводов для различного промышленного оборудования по всему миру.",
      cols: [
        { title: "Продукты", links: ["Контроллер IMM","Серводвигатель","Преобразователь Частоты","Комплекты Модернизации"] },
        { title: "Компания", links: ["О Нас","Новости и События","Блог","Карьера"] },
        { title: "Поддержка", links: ["Контакты","Техническая Документация","Программа АМС","Чат WhatsApp"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. Все права защищены.",
    },
  },
  id: {
    nav: { links: ["Beranda","Solusi","Produk","Tentang","Layanan","Berita","Kontak"], quote: "Minta Penawaran" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Est. 2007", title: ["Solusi Kontrol","Industri","Presisi"], accent: 1, desc: "Kontroler IMM canggih, sistem servo & konverter frekuensi untuk mesin cetak injeksi, pembentukan kawat, mesin pres hidrolik dan stamping di seluruh dunia." },
        { tag: "Solusi Pembentukan Kawat", title: ["Kecepatan Tinggi","Pembentukan","Kawat CNC"], accent: 2, desc: "Pengulangan di bawah mili-meter dengan presisi servo untuk mesin tekuk kawat dan coil pegas dalam lingkungan produksi volume tinggi." },
        { tag: "Stamping & Pembentukan", title: ["Kontrol","Stamping","Cerdas"], accent: 0, desc: "Pemantauan tonase, kontrol langkah dan perlindungan cetakan untuk lini stamping volume tinggi dengan deteksi cacat otomatis." },
        { tag: "Sistem Mesin Pres Hidrolik", title: ["Kontrol Pres","Hidrolik","Canggih"], accent: 2, desc: "Kontrol katup proporsional, gerakan multi-silinder sinkronisasi dan pembuatan profil tekanan untuk lini pembentukan dan penempaan." },
      ],
      exploreBtn: "Jelajahi Produk", watchBtn: "Lihat Demo",
    },
    stats: [["18+","Tahun Keunggulan"],["50+","Kantor Global"],["200+","Paten & IP"],["30K+","Klien Puas"]],
    marquee: ["Kontroler IMM","Sistem Servo","Konverter Frekuensi","Pembentukan Kawat","Cetak Injeksi","Pres Hidrolik","Stamping","Dukungan Global 24/7"],
    solutions: {
      label: "Solusi Kami", title: "Aplikasi", accent: "Industri",
      sub: "Kontroler dan servo canggih untuk mesin cetak injeksi, pembentukan kawat, stamping dan mesin pres hidrolik.",
      cards: [
        { num: "01 / Solusi", title: "Cetak Injeksi", desc: "Kontrol suhu cetakan presisi, manajemen gaya penguncian dan analitik siklus real-time untuk mesin injeksi plastik." },
        { num: "02 / Solusi", title: "Pembentukan Kawat", desc: "Mesin tekuk kawat CNC kecepatan tinggi dengan pengulangan di bawah mili-meter dan presisi servo." },
        { num: "03 / Solusi", title: "Stamping Forming", desc: "Pemantauan tonase, kontrol langkah dan perlindungan cetakan dengan deteksi cacat otomatis." },
        { num: "04 / Solusi", title: "Pres Hidrolik", desc: "Kontrol katup proporsional, gerakan multi-silinder sinkronisasi dan pembuatan profil tekanan." },
      ],
      viewDetails: "Lihat Detail",
    },
    about: {
      label: "Tentang Arcuchi", heading: "Solusi", accent: "Futuristik", headingSuffix: "untuk Manufaktur",
      p1: "Arcuchi, didirikan tahun 2007, adalah pemimpin global yang menyediakan solusi peralatan industri terdepan. Berspesialisasi dalam kontroler dan servo berkualitas tinggi untuk mesin cetak injeksi, pembentukan kawat, mesin pres hidrolik dan stamping.",
      p2: "Didorong oleh R&D internal dan inovasi, kami memberdayakan produsen di seluruh dunia untuk meningkatkan kemampuan produksi dan mencapai kesuksesan berkelanjutan.",
      stats: [{ value: "17+", label: "Tahun Pengalaman" },{ value: "50+", label: "Negara Dilayani" },{ value: "1000+", label: "Mesin Terpasang" }],
      btn: "Lebih Lanjut →",
    },
    services: {
      label: "Layanan Kami", title: "Dukungan", accent: "Purna Jual",
      sub: "Arcuchi memiliki dukungan purna jual paling andal. Sistem layanan kami menjamin kinerja mesin tanpa gangguan.",
      items: [
        { title: "Garansi", desc: "Garansi produsen 1 tahun untuk semua produk dengan pengiriman suku cadang cepat di seluruh dunia." },
        { title: "Layanan 24×7", desc: "Dukungan teknik sepanjang waktu melalui telepon, panggilan video dan diagnostik jarak jauh." },
        { title: "Upgrade Mesin", desc: "Solusi retrofit untuk memodernisasi mesin yang ada dengan arsitektur kontrol baru dan konektivitas IoT." },
        { title: "AMC Pemeliharaan", desc: "Kontrak pemeliharaan tahunan dengan diagnostik prediktif dan SLA respons prioritas." },
      ],
    },
    testimonials: {
      label: "Ulasan Klien", title: "Apa Kata", accent: "Klien",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "Meksiko", text: "Kontroler IMM Arcuchi meningkatkan efisiensi kami. Antarmuka yang mudah digunakan dan pemantauan jarak jauh sangat meningkatkan produksi kami." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Jepang", text: "Kontroler sistem servo Arcuchi merevolusi operasi kami. Kontrol yang ditingkatkan, waktu henti berkurang secara signifikan di semua lini." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Brasil", text: "Presisi bertemu inovasi dengan Kontroler PLC Arcuchi. Kualitas meningkat, pelatihan mudah untuk seluruh tim kami." },
      ],
    },
    news: {
      label: "Berita & Acara", title: "Update", accent: "Terbaru", viewAll: "Lihat Semua Artikel →",
      readArticle: "Baca Artikel", readMore: "Baca Selengkapnya",
      articles: [
        { tag: "Teknologi", date: "5 Juni 2024", title: "Fitur Sistem Servo", excerpt: "Presisi dan efisiensi sangat penting dalam aplikasi industri seperti robotika, mesin CNC, dan cetak injeksi. Temukan bagaimana sistem servo modern merevolusi lantai manufaktur." },
        { tag: "Sistem Kontrol", date: "20 Mei 2024", title: "Cara Memilih Kontroler PLC Terbaik", excerpt: "Memilih kontroler yang tepat sangat penting untuk mengoptimalkan kinerja dan keandalan mesin cetak injeksi Anda." },
        { tag: "Hidrolik", date: "18 April 2024", title: "Apa Itu Sistem Servo AC Hidrolik?", excerpt: "Sistem servo AC hidrolik menggunakan cairan hidrolik untuk mentransmisikan daya dan mengendalikan gerakan, menawarkan kinerja tinggi." },
      ],
    },
    partners: "Dipercaya oleh produsen terkemuka di seluruh dunia",
    contact: {
      label: "Hubungi Kami", title: "Kontak", accent: "Kami",
      desc: "Baik Anda membutuhkan solusi standar atau sistem yang sepenuhnya disesuaikan, insinyur kami siap membantu mengoptimalkan lini produksi Anda.",
      infoLabels: { phone: "Telepon", email: "Email", address: "Alamat", support: "Dukungan" },
      supportVal: "24 Jam / 7 Hari Seminggu",
      form: { nameLbl: "Nama Anda", namePlh: "Nama lengkap", emailLbl: "Alamat Email", emailPlh: "name@company.com", productLbl: "Produk yang Diminati", productPlh: "Pilih produk", products: ["Kontroler IMM","Sistem Servo","Konverter Frekuensi","Retrofit Mesin"], msgLbl: "Pesan", msgPlh: "Jelaskan aplikasi atau kebutuhan Anda", sendBtn: "Kirim Pesan →" },
    },
    footer: {
      desc: "Tingkatkan efisiensi produksi Anda dengan kontroler dan servo canggih untuk berbagai peralatan industri di seluruh dunia.",
      cols: [
        { title: "Produk", links: ["Kontroler IMM","Sistem Servo","Konverter Frekuensi","Kit Retrofit"] },
        { title: "Perusahaan", links: ["Tentang Kami","Berita & Acara","Blog","Karier"] },
        { title: "Dukungan", links: ["Hubungi Kami","Dokumen Teknis","Program AMC","Chat WhatsApp"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. Hak cipta dilindungi.",
    },
  },
  pt: {
    nav: { links: ["Início","Soluções","Produtos","Sobre","Serviços","Notícias","Contato"], quote: "Solicitar Cotação" },
    hero: {
      slides: [
        { tag: "Shenzhen Arcuchi Technology — Fund. 2007", title: ["Soluções","de Controle","Industrial"], accent: 1, desc: "Controladores IMM avançados, sistemas servo e conversores de frequência para moldagem por injeção, formação de arame, prensas hidráulicas e máquinas de estampagem em todo o mundo." },
        { tag: "Soluções de Formação de Arame", title: ["Alta Velocidade","Formação de","Arame CNC"], accent: 2, desc: "Repetibilidade sub-milimétrica com precisão servo para dobradeiras de arame e bobinadeiras em ambientes de produção de alto volume." },
        { tag: "Estampagem e Formação", title: ["Controle","de Estampagem","Inteligente"], accent: 0, desc: "Monitoramento de tonelagem, controle de curso e proteção de molde para linhas de estampagem de alto volume com detecção automática de defeitos." },
        { tag: "Sistemas de Prensa Hidráulica", title: ["Controle Avançado","de Prensa","Hidráulica"], accent: 2, desc: "Controle de válvula proporcional, movimento multi-cilindro sincronizado e perfil de pressão para linhas de formação e forjamento." },
      ],
      exploreBtn: "Explorar Produtos", watchBtn: "Ver Demo",
    },
    stats: [["18+","Anos de Excelência"],["50+","Escritórios Globais"],["200+","Patentes & PI"],["30K+","Clientes Satisfeitos"]],
    marquee: ["Controlador IMM","Sistema Servo","Conversor de Frequência","Formação de Arame","Moldagem por Injeção","Prensa Hidráulica","Estampagem","Suporte Global 24/7"],
    solutions: {
      label: "Nossas Soluções", title: "Aplicações", accent: "Industriais",
      sub: "Controladores e servos avançados para moldagem por injeção, formação de arame, estampagem e prensas hidráulicas.",
      cards: [
        { num: "01 / Solução", title: "Moldagem por Injeção", desc: "Controle preciso de temperatura do molde, gestão da força de fechamento e análise de ciclo em tempo real." },
        { num: "02 / Solução", title: "Formação de Arame", desc: "Dobradeiras CNC de alta velocidade com repetibilidade sub-milimétrica e precisão servo." },
        { num: "03 / Solução", title: "Estampagem", desc: "Monitoramento de tonelagem, controle de curso e proteção de molde com detecção automática de defeitos." },
        { num: "04 / Solução", title: "Prensa Hidráulica", desc: "Controle de válvula proporcional, movimento multi-cilindro sincronizado e perfil de pressão." },
      ],
      viewDetails: "Ver Detalhes",
    },
    about: {
      label: "Sobre a Arcuchi", heading: "Soluções", accent: "Futuristas", headingSuffix: "para a Manufatura",
      p1: "A Arcuchi, fundada em 2007, é líder global no fornecimento de soluções avançadas para equipamentos industriais. Especializada em controladores e servos de alta qualidade para moldagem por injeção, formação de arame, prensas hidráulicas e estampagem.",
      p2: "Impulsionada por P&D interno e inovação, capacitamos fabricantes em todo o mundo a aprimorar suas capacidades de produção e alcançar sucesso sustentável.",
      stats: [{ value: "17+", label: "Anos de Experiência" },{ value: "50+", label: "Países Atendidos" },{ value: "1000+", label: "Máquinas Instaladas" }],
      btn: "Saiba Mais →",
    },
    services: {
      label: "Nosso Serviço", title: "Suporte", accent: "Pós-Venda",
      sub: "A Arcuchi tem o suporte pós-venda mais confiável. Nosso sistema de serviço garante desempenho ininterrupto da máquina.",
      items: [
        { title: "Garantia", desc: "Garantia de 1 ano do fabricante para todos os produtos com envio rápido de peças de reposição." },
        { title: "Serviço 24×7", desc: "Suporte de engenharia 24h por telefone, videochamada e diagnóstico remoto." },
        { title: "Upgrade de Máquina", desc: "Soluções de retrofit para modernizar máquinas existentes com nova arquitetura de controle e conectividade IoT." },
        { title: "AMC de Manutenção", desc: "Contratos de manutenção anual com diagnósticos preditivos e SLAs de resposta prioritária." },
      ],
    },
    testimonials: {
      label: "Opinião de Clientes", title: "O que dizem", accent: "os Clientes",
      items: [
        { initials: "LM", name: "Luis Martinez", country: "México", text: "O Controlador IMM da Arcuchi aumentou nossa eficiência. Interface fácil e monitoramento remoto melhoraram nossa produção imensamente." },
        { initials: "HT", name: "Hiroshi Tanaka", country: "Japão", text: "O controlador servo da Arcuchi revolucionou nossas operações. Controle aprimorado e tempo de inatividade reduzido significativamente." },
        { initials: "CR", name: "Carlos Rodrigues", country: "Brasil", text: "Precisão encontra inovação com o Controlador PLC da Arcuchi. Qualidade melhorada e treinamento facilitado para toda a equipe." },
      ],
    },
    news: {
      label: "Notícias & Eventos", title: "Últimas", accent: "Atualizações", viewAll: "Ver Todos os Artigos →",
      readArticle: "Ler Artigo", readMore: "Leia Mais",
      articles: [
        { tag: "Tecnologia", date: "5 Junho 2024", title: "Características de um Sistema Servo", excerpt: "Precisão e eficiência são fundamentais em aplicações industriais como robótica, maquinário CNC e moldagem por injeção. Descubra como os sistemas servo modernos estão revolucionando a manufatura." },
        { tag: "Sistemas de Controle", date: "20 Maio 2024", title: "Como Escolher o Melhor Controlador PLC", excerpt: "Escolher o controlador certo é crucial para otimizar o desempenho e a confiabilidade da sua máquina de moldagem por injeção." },
        { tag: "Hidráulica", date: "18 Abril 2024", title: "O que é um Sistema Servo AC Hidráulico?", excerpt: "Um sistema servo AC hidráulico usa fluido hidráulico para transmitir potência e controlar movimento, oferecendo alto desempenho." },
      ],
    },
    partners: "Confiado por fabricantes líderes em todo o mundo",
    contact: {
      label: "Entre em Contato", title: "Contate", accent: "-nos",
      desc: "Seja uma solução padrão ou sistema totalmente personalizado, nossos engenheiros estão prontos para otimizar sua linha de produção.",
      infoLabels: { phone: "Telefone", email: "E-mail", address: "Endereço", support: "Suporte" },
      supportVal: "24 Horas / 7 Dias por Semana",
      form: { nameLbl: "Seu Nome", namePlh: "Nome completo", emailLbl: "Endereço de E-mail", emailPlh: "nome@empresa.com", productLbl: "Produto de Interesse", productPlh: "Selecione um produto", products: ["Controlador IMM","Sistema Servo","Conversor de Frequência","Retrofit de Máquina"], msgLbl: "Mensagem", msgPlh: "Descreva sua aplicação ou requisitos", sendBtn: "Enviar Mensagem →" },
    },
    footer: {
      desc: "Melhore a eficiência da sua produção com controladores e servos avançados para vários equipamentos industriais em todo o mundo.",
      cols: [
        { title: "Produtos", links: ["Controlador IMM","Sistema Servo","Conversor de Frequência","Kits de Retrofit"] },
        { title: "Empresa", links: ["Sobre Nós","Notícias & Eventos","Blog","Carreiras"] },
        { title: "Suporte", links: ["Contate-nos","Documentação Técnica","Programa AMC","Chat WhatsApp"] },
      ],
      copy: "© 2025 Shenzhen Arcuchi Technology Co., Ltd. Todos os direitos reservados.",
    },
  },
};

// ─── Responsive Hook ─────────────────────────────────────────────────────────

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

// ─── Shared Styles ──────────────────────────────────────────────────────────

const fonts = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Open+Sans:wght@300;400;600&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Open Sans', sans-serif; background: #f0f2f5; color: #1a2233; }
  html { scroll-behavior: smooth; }
  @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
  @keyframes countUp { from { opacity: 0; } to { opacity: 1; } }
`;

// ─── Components ─────────────────────────────────────────────────────────────

function LanguageSwitcher({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find(l => l.code === lang);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer",
        padding: "6px 12px", fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
        letterSpacing: "0.06em", borderRadius: 2, transition: "background 0.2s",
      }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
      >
        <span style={{ fontSize: 15 }}>{current.flag}</span>
        <span>{current.code.toUpperCase()}</span>
        <span style={{ fontSize: 9, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: COLORS.navyDark, border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 12px 36px rgba(0,0,0,0.5)", zIndex: 200, minWidth: 160,
          borderRadius: 2, overflow: "hidden",
        }}>
          {LANGUAGES.map(l => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }} style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "10px 14px", background: l.code === lang ? "rgba(204,0,0,0.2)" : "transparent",
              border: "none", cursor: "pointer", color: l.code === lang ? "#fff" : "rgba(255,255,255,0.65)",
              fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 600, textAlign: "left",
              borderLeft: l.code === lang ? `3px solid ${COLORS.red}` : "3px solid transparent",
              transition: "background 0.15s",
            }}
              onMouseEnter={e => { if (l.code !== lang) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
              onMouseLeave={e => { if (l.code !== lang) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 16 }}>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TopBar() {
  const w = useWindowWidth();
  if (w < 768) return null;
  return (
    <div style={{ background: COLORS.navy, padding: "6px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", gap: 6 }}>
        📍 Shangzhi Technology Park, Guangming, Shenzhen, China
      </span>
      <div style={{ display: "flex", gap: 28 }}>
        {[["📞", "+86 138 2527 1417"], ["✉️", "marketing@arcuchicnc.com"]].map(([icon, val]) => (
          <span key={val} style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", gap: 5 }}>
            {icon} {val}
          </span>
        ))}
      </div>
    </div>
  );
}

function Navbar({ activeSection, lang, setLang }) {
  const t = TRANSLATIONS[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const w = useWindowWidth();
  const isMobile = w < 768;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  const navKeys = ["home","solutions","/products","/about","services","/news","/contact"];

  return (
    <>
      <nav style={{
        background: scrolled ? "rgba(0,31,68,0.97)" : COLORS.navyDark,
        backdropFilter: "blur(10px)",
        padding: isMobile ? "0 20px" : "0 40px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 64, position: "sticky", top: 0, zIndex: 100,
        borderBottom: "1px solid rgba(204,0,0,0.25)", transition: "background 0.3s",
      }}>
        <img src={IMAGES.logoWhite} alt="Arcuchi Logo" style={{ height: 36, objectFit: "contain" }} />

        {!isMobile && (
          <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
            {t.links.map((link, i) => (
              <a key={link} href={`#${navKeys[i]}`} style={{
                fontFamily: "Montserrat, sans-serif", fontSize: 11, fontWeight: 700,
                letterSpacing: "0.1em",
                color: activeSection === navKeys[i] ? "#fff" : "rgba(255,255,255,0.65)",
                textDecoration: "none", textTransform: "uppercase", paddingBottom: 2,
                borderBottom: activeSection === navKeys[i] ? `2px solid ${COLORS.red}` : "2px solid transparent",
                transition: "color 0.2s, border-color 0.2s",
              }}>
                {link}
              </a>
            ))}
          </div>
        )}

        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LanguageSwitcher lang={lang} setLang={setLang} />
            <button style={{
              background: COLORS.red, color: "#fff", fontFamily: "Montserrat, sans-serif",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
              padding: "10px 22px", border: "none", cursor: "pointer",
            }}>
              {t.quote}
            </button>
          </div>
        )}

        {isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <LanguageSwitcher lang={lang} setLang={setLang} />
            <button onClick={() => setMenuOpen(o => !o)} style={{
              background: "none", border: "none", cursor: "pointer", padding: 4,
              display: "flex", flexDirection: "column", gap: 5,
            }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  display: "block", width: 24, height: 2, background: "#fff",
                  transition: "transform 0.3s, opacity 0.3s",
                  transform: menuOpen
                    ? i === 0 ? "rotate(45deg) translate(5px,5px)"
                    : i === 2 ? "rotate(-45deg) translate(5px,-5px)" : "none"
                    : "none",
                  opacity: menuOpen && i === 1 ? 0 : 1,
                }} />
              ))}
            </button>
          </div>
        )}
      </nav>

      {isMobile && (
        <div style={{
          position: "fixed", top: 64, left: 0, right: 0, zIndex: 99,
          background: COLORS.navyDark,
          maxHeight: menuOpen ? 400 : 0,
          overflow: "hidden", transition: "max-height 0.4s ease",
          borderBottom: menuOpen ? `2px solid ${COLORS.red}` : "none",
        }}>
          {t.links.map((link, i) => (
            <a key={link} href={`#${navKeys[i]}`} onClick={() => setMenuOpen(false)} style={{
              display: "block", padding: "14px 24px",
              fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
              color: activeSection === navKeys[i] ? "#fff" : "rgba(255,255,255,0.65)",
              textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.1em",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}>
              {link}
            </a>
          ))}
          <div style={{ padding: 16 }}>
            <button style={{
              width: "100%", background: COLORS.red, color: "#fff",
              fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
              letterSpacing: "0.1em", textTransform: "uppercase", padding: 13, border: "none", cursor: "pointer",
            }}>
              {t.quote}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Hero() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].hero;
  const w = useWindowWidth();
  const isMobile = w < 768;

  const slideImgs = [IMAGES.inject, IMAGES.wire, IMAGES.stamping, IMAGES.hydraulic];
  const slides = t.slides.map((s, i) => ({ ...s, img: slideImgs[i] }));

  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = (index) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 600);
  };

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = () => goTo((current + 1) % slides.length);

  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % slides.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides[current];

  return (
    <section id="home" style={{ position: "relative", overflow: "hidden", height: isMobile ? 520 : 580 }}>
      {slides.map((s, i) => (
        <div key={i} style={{
          position: "absolute", inset: 0, zIndex: 0,
          opacity: i === current ? 1 : 0, transition: "opacity 0.8s ease",
        }}>
          <img src={s.img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      ))}

      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(110deg,rgba(0,15,40,0.95) 40%,rgba(0,30,70,0.6))", zIndex: 1 }} />

      <div style={{
        position: "relative", zIndex: 2, height: "100%",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        alignItems: "center",
      }}>
        <div style={{
          padding: isMobile ? "48px 24px 80px" : "72px 40px 72px 48px",
          opacity: animating ? 0 : 1,
          transform: animating ? "translateY(12px)" : "translateY(0)",
          transition: "opacity 0.4s ease, transform 0.4s ease",
        }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(204,0,0,0.15)", border: "1px solid rgba(204,0,0,0.4)",
            padding: "5px 14px", marginBottom: 20,
          }}>
            <span style={{ width: 6, height: 6, background: COLORS.red, borderRadius: "50%", animation: "pulse 2s infinite" }} />
            <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 9 : 10, color: "#ff9999", letterSpacing: "0.2em", fontWeight: 700, textTransform: "uppercase" }}>
              {slide.tag}
            </span>
          </div>
          <h1 style={{
            fontFamily: "Montserrat,sans-serif",
            fontSize: isMobile ? "clamp(28px,7vw,38px)" : "clamp(32px,3.5vw,50px)",
            fontWeight: 900, color: "#fff", lineHeight: 1.1, marginBottom: 16,
            textTransform: "uppercase", letterSpacing: "0.02em",
          }}>
            {slide.title.map((line, i) => (
              <span key={i}>
                {i === slide.accent ? <span style={{ color: COLORS.red }}>{line}</span> : line}
                {i < slide.title.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p style={{ fontSize: isMobile ? 12 : 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.8, maxWidth: 420, marginBottom: 32, fontWeight: 300 }}>
            {slide.desc}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button style={{ background: COLORS.red, color: "#fff", fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "13px 30px", border: "none", cursor: "pointer" }}>
              {t.exploreBtn}
            </button>
            <button style={{ background: "transparent", color: "#fff", fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", padding: "12px 24px", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer" }}>
              {t.watchBtn}
            </button>
          </div>
        </div>

        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 40px 40px 0" }}>
            <img src={IMAGES.controllerUnit} alt="Arcuchi Controller Unit" style={{ width: "100%", maxWidth: 380, objectFit: "contain", filter: "drop-shadow(0 16px 56px rgba(0,0,0,0.6))" }} />
          </div>
        )}
      </div>

      {[["‹", prev, "left", isMobile ? 10 : 20], ["›", next, "right", isMobile ? 10 : 20]].map(([icon, fn, side, offset]) => (
        <button key={side} onClick={fn} style={{
          position: "absolute", top: "50%", [side]: offset, zIndex: 3,
          transform: "translateY(-50%)",
          background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)",
          color: "#fff", width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
          fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(4px)", transition: "background 0.2s",
        }}
          onMouseEnter={e => e.currentTarget.style.background = COLORS.red}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(0,0,0,0.35)"}
        >
          {icon}
        </button>
      ))}

      <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 3, display: "flex", gap: 8 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{
            width: i === current ? 28 : 8, height: 8,
            background: i === current ? COLORS.red : "rgba(255,255,255,0.35)",
            border: "none", cursor: "pointer", padding: 0,
            transition: "width 0.3s ease, background 0.3s ease",
          }} />
        ))}
      </div>

      {!isMobile && (
        <div style={{ position: "absolute", bottom: 28, right: 48, zIndex: 3, fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em" }}>
          {String(current + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </div>
      )}
    </section>
  );
}

function StatCounter({ num, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const target = parseInt(num.replace(/\D/g, ""));
  const suffix = num.replace(/[\d]/g, "");

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = Math.ceil(target / 40);
        const timer = setInterval(() => {
          start = Math.min(start + step, target);
          setCount(start);
          if (start >= target) clearInterval(timer);
        }, 40);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} style={{ textAlign: "center", padding: "24px 20px", borderRight: "1px solid rgba(255,255,255,0.07)" }}>
      <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 40, fontWeight: 900, color: COLORS.red, lineHeight: 1 }}>
        {count}{suffix}
      </div>
      <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 5, letterSpacing: "0.1em", textTransform: "uppercase" }}>
        {label}
      </div>
    </div>
  );
}

function StatsBar() {
  const lang = useLang();
  const stats = TRANSLATIONS[lang].stats;
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <div style={{ background: COLORS.navyDark, display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)" }}>
      {stats.map(([num, label]) => <StatCounter key={num} num={num} label={label} />)}
    </div>
  );
}

function Marquee() {
  const lang = useLang();
  const items = TRANSLATIONS[lang].marquee;
  const doubled = [...items, ...items];
  return (
    <div style={{ background: COLORS.red, padding: "10px 0", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 40, whiteSpace: "nowrap", animation: "marquee 22s linear infinite" }}>
        {doubled.map((item, i) => (
          <span key={i} style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "#fff", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 4, height: 4, background: "rgba(255,255,255,0.5)", borderRadius: "50%", flexShrink: 0, display: "inline-block" }} />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ label, title, accent, sub }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 28, height: 2, background: COLORS.red }} />
        <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.red, letterSpacing: "0.22em", textTransform: "uppercase" }}>{label}</span>
      </div>
      <h2 style={{ fontFamily: "Montserrat,sans-serif", fontSize: "clamp(22px,2.5vw,32px)", fontWeight: 800, color: COLORS.navy, textTransform: "uppercase", letterSpacing: "0.03em", lineHeight: 1.2 }}>
        {title} <span style={{ color: COLORS.red }}>{accent}</span>
      </h2>
      {sub && <p style={{ fontSize: 13, color: COLORS.greyDark, marginTop: 8, maxWidth: 560, lineHeight: 1.75 }}>{sub}</p>}
    </div>
  );
}

function SolutionCard({ num, title, desc, img }) {
  const lang = useLang();
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href="#/products"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: "block", textDecoration: "none", position: "relative", overflow: "hidden", cursor: "pointer", height: 420 }}
    >
      <img src={img} alt={title} style={{
        position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
        transform: hovered ? "scale(1.08)" : "scale(1)", transition: "transform 0.6s ease",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: hovered
          ? "linear-gradient(to top, rgba(0,10,25,0.95) 40%, rgba(0,10,25,0.35) 100%)"
          : "linear-gradient(to top, rgba(0,10,25,0.88) 35%, rgba(0,10,25,0.15) 100%)",
        transition: "background 0.4s ease",
      }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, height: 3, background: COLORS.red, width: hovered ? "100%" : "0%", transition: "width 0.4s ease" }} />
      <div style={{
        position: "absolute", top: 22, left: 22,
        fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700,
        color: "rgba(255,255,255,0.55)", letterSpacing: "0.2em", textTransform: "uppercase",
        background: "rgba(0,0,0,0.3)", padding: "4px 10px", backdropFilter: "blur(4px)",
      }}>
        {num}
      </div>
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, padding: "28px 26px",
        transform: hovered ? "translateY(0)" : "translateY(6px)", transition: "transform 0.35s ease",
      }}>
        <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 20, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2, marginBottom: 10 }}>
          {title}
        </div>
        <p style={{
          fontSize: 12, color: "rgba(255,255,255,0.72)", lineHeight: 1.75, maxWidth: 340,
          opacity: hovered ? 1 : 0, transform: hovered ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 0.35s ease 0.05s, transform 0.35s ease 0.05s",
        }}>
          {desc}
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
          color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 14,
          opacity: hovered ? 1 : 0, transform: hovered ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 0.35s ease 0.1s, transform 0.35s ease 0.1s",
        }}>
          {TRANSLATIONS[lang].solutions.viewDetails} <span style={{ display: "inline-block" }}>→</span>
        </div>
      </div>
    </a>
  );
}

function Solutions() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].solutions;
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w < 1024;
  const cols = isMobile ? "repeat(1,1fr)" : isTablet ? "repeat(2,1fr)" : "repeat(4,1fr)";
  const cardImgs = [IMAGES.inject, IMAGES.wire, IMAGES.stamping, IMAGES.hydraulic];
  const cards = t.cards.map((c, i) => ({ ...c, img: cardImgs[i] }));
  return (
    <section id="solutions" style={{ padding: isMobile ? "48px 20px" : "72px 40px", background: COLORS.greyPage }}>
      <SectionHeader label={t.label} title={t.title} accent={t.accent} sub={t.sub} />
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: 3 }}>
        {cards.map(c => <SolutionCard key={c.title} {...c} />)}
      </div>
    </section>
  );
}

function About() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].about;
  const w = useWindowWidth();
  const isMobile = w < 768;
  const stats = t.stats;
  return (
    <section id="about" style={{
      background: COLORS.navyDark,
      padding: isMobile ? "56px 20px" : "80px 40px",
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: isMobile ? 40 : 64,
        alignItems: "center",
        maxWidth: 1200,
        margin: "0 auto",
      }}>
        {/* Image column */}
        <div style={{ position: "relative" }}>
          {/* Decorative red border offset */}
          <div style={{
            position: "absolute", top: -12, left: -12,
            width: "60%", height: "60%",
            border: `3px solid ${COLORS.red}`,
            zIndex: 0,
            display: isMobile ? "none" : "block",
          }} />
          <div style={{ position: "relative", zIndex: 1, overflow: "hidden" }}>
            <img
              src={IMAGES.factory}
              alt="Arcuchi Factory"
              style={{ width: "100%", height: isMobile ? 240 : 400, objectFit: "cover", display: "block" }}
            />
            {/* Overlay gradient */}
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(135deg, rgba(0,51,102,0.3) 0%, transparent 60%)",
            }} />
          </div>
          {/* Floating since badge */}
          <div style={{
            position: "absolute", bottom: -20, right: isMobile ? 0 : -20,
            background: COLORS.red, padding: "18px 24px", zIndex: 2, textAlign: "center",
          }}>
            <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1 }}>2007</div>
            <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.8)", letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 4 }}>Est.</div>
          </div>
        </div>

        {/* Content column */}
        <div style={{ paddingBottom: isMobile ? 0 : 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 32, height: 2, background: COLORS.red }} />
            <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(255,150,150,0.85)", letterSpacing: "0.22em", textTransform: "uppercase" }}>{t.label}</span>
          </div>
          <h2 style={{
            fontFamily: "Montserrat,sans-serif", fontSize: "clamp(22px,2.6vw,32px)",
            fontWeight: 800, color: "#fff", textTransform: "uppercase",
            letterSpacing: "0.03em", lineHeight: 1.2, margin: 0,
          }}>
            {t.heading} <span style={{ color: COLORS.red }}>{t.accent}</span><br />{t.headingSuffix}
          </h2>

          {/* Divider */}
          <div style={{ width: 48, height: 3, background: COLORS.red, margin: "20px 0" }} />

          <p style={{ fontSize: 13.5, color: "rgba(255,255,255,0.68)", lineHeight: 1.9, fontWeight: 300, margin: 0 }}>
            {t.p1}
          </p>
          <p style={{ fontSize: 13.5, color: "rgba(255,255,255,0.68)", lineHeight: 1.9, fontWeight: 300, margin: "14px 0 0" }}>
            {t.p2}
          </p>

          {/* Stats row */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3,1fr)",
            gap: 0, marginTop: 32, borderTop: "1px solid rgba(255,255,255,0.1)",
            borderLeft: "1px solid rgba(255,255,255,0.1)",
          }}>
            {stats.map(s => (
              <div key={s.label} style={{
                padding: "18px 0 18px 20px",
                borderRight: "1px solid rgba(255,255,255,0.1)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}>
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: "clamp(20px,2vw,26px)", fontWeight: 900, color: COLORS.red, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 5 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <button style={{
            marginTop: 32, background: "transparent", color: "#fff",
            fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
            letterSpacing: "0.12em", textTransform: "uppercase",
            padding: "13px 30px", border: `2px solid ${COLORS.red}`,
            cursor: "pointer", transition: "background 0.25s, color 0.25s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = COLORS.red; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            {t.btn}
          </button>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ icon, title, desc }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: COLORS.white, padding: "28px 20px", textAlign: "center", borderTop: `3px solid ${hovered ? COLORS.red : COLORS.navy}`, transition: "border-color 0.25s" }}
    >
      <div style={{ width: 56, height: 56, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src={icon} alt={title} style={{ width: 48, height: 48, objectFit: "contain" }} />
      </div>
      <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.navy, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{title}</div>
      <p style={{ fontSize: 12, color: COLORS.greyDark, lineHeight: 1.65 }}>{desc}</p>
    </div>
  );
}

function Services() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].services;
  const w = useWindowWidth();
  const isMobile = w < 768;
  const icons = [IMAGES.iconWarranty, IMAGES.icon247, IMAGES.iconUpgrade, IMAGES.iconMaint];
  const services = t.items.map((s, i) => ({ ...s, icon: icons[i] }));
  return (
    <section id="services" style={{ padding: isMobile ? "48px 20px" : "72px 40px", background: COLORS.greyCard }}>
      <SectionHeader label={t.label} title={t.title} accent={t.accent} sub={t.sub} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 16 }}>
        {services.map(s => <ServiceCard key={s.title} {...s} />)}
      </div>
    </section>
  );
}

function TestiCard({ initials, name, country, text }) {
  return (
    <div style={{ background: COLORS.white, padding: "28px", borderLeft: `3px solid ${COLORS.red}` }}>
      <div style={{ color: COLORS.red, fontSize: 14, letterSpacing: 3, marginBottom: 10 }}>★★★★★</div>
      <p style={{ fontSize: 13, color: COLORS.textMid, lineHeight: 1.8, fontStyle: "italic", marginBottom: 18 }}>"{text}"</p>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 38, height: 38, background: COLORS.navy, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Montserrat,sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{initials}</div>
        <div>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.navy, textTransform: "uppercase", letterSpacing: "0.06em" }}>{name}</div>
          <div style={{ fontSize: 11, color: COLORS.greyDark }}>{country}</div>
        </div>
      </div>
    </div>
  );
}

function Testimonials() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].testimonials;
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <section style={{ padding: isMobile ? "48px 20px" : "72px 40px", background: COLORS.greyPage }}>
      <SectionHeader label={t.label} title={t.title} accent={t.accent} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 20 }}>
        {t.items.map(item => <TestiCard key={item.name} {...item} />)}
      </div>
    </section>
  );
}

function NewsFeaturedCard({ img, date, tag, title, excerpt, hovered, onEnter, onLeave, readLabel, slug }) {
  return (
    <a
      href={slug ? `#/news/${slug}` : "#/news"}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        display: "block", textDecoration: "none",
        position: "relative", overflow: "hidden", cursor: "pointer", borderRadius: 4,
        height: "100%", minHeight: 420,
        boxShadow: hovered ? "0 20px 48px rgba(0,0,0,0.35)" : "0 8px 24px rgba(0,0,0,0.2)",
        transform: hovered ? "translateY(-4px)" : "none",
        transition: "transform 0.3s, box-shadow 0.3s",
      }}
    >
      <img src={img} alt={title} style={{
        position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
        transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.5s",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to top, rgba(0,15,40,0.97) 0%, rgba(0,20,55,0.7) 45%, rgba(0,0,0,0.1) 100%)",
      }} />
      <div style={{ position: "absolute", inset: 0, padding: 28, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{
            background: COLORS.red, color: "#fff", fontFamily: "Montserrat,sans-serif",
            fontSize: 9, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase",
            padding: "4px 10px", borderRadius: 2,
          }}>{tag}</span>
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em" }}>{date}</span>
        </div>
        <div style={{
          fontFamily: "Montserrat,sans-serif", fontSize: "clamp(16px,1.6vw,20px)", fontWeight: 800,
          color: "#fff", lineHeight: 1.35, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 12,
        }}>{title}</div>
        <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: 18 }}>{excerpt}</p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 800,
          color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.1em",
        }}>
          {readLabel || "Read Article"}
          <span style={{ transform: hovered ? "translateX(5px)" : "none", transition: "transform 0.25s", display: "inline-block" }}>→</span>
        </div>
      </div>
    </a>
  );
}

function NewsSmallCard({ img, date, tag, title, hovered, onEnter, onLeave, isMobile, readLabel, slug }) {
  if (isMobile) {
    return (
      <a
        href={slug ? `#/news/${slug}` : "#/news"}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        style={{
          display: "block", textDecoration: "none",
          overflow: "hidden", cursor: "pointer", borderRadius: 6,
          background: COLORS.navyDark,
          boxShadow: hovered ? "0 10px 28px rgba(0,0,0,0.25)" : "0 3px 10px rgba(0,0,0,0.12)",
          transform: hovered ? "translateY(-3px)" : "none",
          transition: "transform 0.3s, box-shadow 0.3s",
        }}
      >
        <div style={{ overflow: "hidden", height: 130, position: "relative" }}>
          <img src={img} alt={title} style={{
            width: "100%", height: "100%", objectFit: "cover",
            transform: hovered ? "scale(1.07)" : "scale(1)", transition: "transform 0.5s",
          }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,15,40,0.6), transparent 60%)" }} />
          <span style={{
            position: "absolute", top: 10, left: 10,
            background: COLORS.red, color: "#fff",
            fontFamily: "Montserrat,sans-serif", fontSize: 8, fontWeight: 800,
            letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 2,
          }}>{tag}</span>
        </div>
        <div style={{ padding: "14px 14px 16px" }}>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9, color: "rgba(255,255,255,0.45)", letterSpacing: "0.06em", marginBottom: 6 }}>{date}</div>
          <div style={{
            fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
            color: "#fff", lineHeight: 1.4, textTransform: "uppercase", letterSpacing: "0.02em", marginBottom: 10,
          }}>{title}</div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontFamily: "Montserrat,sans-serif", fontSize: 9.5, fontWeight: 700,
            color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.08em",
          }}>
            {readLabel || "Read More"} <span style={{ transform: hovered ? "translateX(3px)" : "none", transition: "transform 0.25s", display: "inline-block" }}>→</span>
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={slug ? `#/news/${slug}` : "#/news"}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        display: "flex", gap: 0, overflow: "hidden", cursor: "pointer", borderRadius: 4,
        background: COLORS.navyDark, textDecoration: "none",
        boxShadow: hovered ? "0 12px 32px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.15)",
        transform: hovered ? "translateX(4px)" : "none",
        transition: "transform 0.3s, box-shadow 0.3s",
        flex: 1,
      }}
    >
      <div style={{ width: 300, minWidth: 300, overflow: "hidden", position: "relative" }}>
        <img src={img} alt={title} style={{
          width: "100%", height: "100%", objectFit: "cover",
          transform: hovered ? "scale(1.08)" : "scale(1)", transition: "transform 0.5s",
        }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, transparent, rgba(0,15,40,0.3))" }} />
      </div>
      <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{
            background: "rgba(204,0,0,0.15)", color: COLORS.red, border: `1px solid rgba(204,0,0,0.3)`,
            fontFamily: "Montserrat,sans-serif", fontSize: 9, fontWeight: 800,
            letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 2,
          }}>{tag}</span>
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9.5, color: "rgba(255,255,255,0.45)", letterSpacing: "0.06em" }}>{date}</span>
        </div>
        <div style={{
          fontFamily: "Montserrat,sans-serif", fontSize: 12.5, fontWeight: 700,
          color: "#fff", lineHeight: 1.4, textTransform: "uppercase", letterSpacing: "0.025em", marginBottom: 10,
        }}>{title}</div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700,
          color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          Read More <span style={{ transform: hovered ? "translateX(4px)" : "none", transition: "transform 0.25s", display: "inline-block" }}>→</span>
        </div>
      </div>
    </a>
  );
}

function News() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].news;
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w < 1024;
  const [hovered, setHovered] = useState(null);

  const articleImgs = [IMAGES.blogServo, IMAGES.blogPlc, IMAGES.blogHydraulic];
  const articles = t.articles.map((a, i) => ({ ...a, img: articleImgs[i], slug: NEWS_ARTICLES[i]?.slug }));

  return (
    <section id="news" style={{ padding: isMobile ? "52px 20px" : "80px 40px", background: COLORS.greyPage }}>
      <div style={{
        display: "flex", alignItems: isMobile ? "flex-start" : "flex-end",
        justifyContent: "space-between", flexDirection: isMobile ? "column" : "row",
        gap: 16, marginBottom: isMobile ? 32 : 44,
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 2, background: COLORS.red }} />
            <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.red, letterSpacing: "0.22em", textTransform: "uppercase" }}>{t.label}</span>
          </div>
          <h2 style={{ fontFamily: "Montserrat,sans-serif", fontSize: "clamp(24px,2.8vw,36px)", fontWeight: 800, color: COLORS.navy, textTransform: "uppercase", letterSpacing: "0.03em", margin: 0, lineHeight: 1.15 }}>
            {t.title} <span style={{ color: COLORS.red }}>{t.accent}</span>
          </h2>
        </div>
        <a href="#/news" style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
          color: COLORS.greyDark, textTransform: "uppercase", letterSpacing: "0.12em",
          textDecoration: "none", border: `1px solid ${COLORS.greyMid}`,
          padding: "10px 20px", borderRadius: 2, whiteSpace: "nowrap", transition: "color 0.2s, border-color 0.2s",
        }}
          onMouseEnter={e => { e.currentTarget.style.color = COLORS.navy; e.currentTarget.style.borderColor = COLORS.navy; }}
          onMouseLeave={e => { e.currentTarget.style.color = COLORS.greyDark; e.currentTarget.style.borderColor = COLORS.greyMid; }}
        >
          {t.viewAll}
        </a>
      </div>

      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ height: 300, borderRadius: 6, overflow: "hidden" }}>
            <NewsFeaturedCard {...articles[0]} readLabel={t.readArticle} hovered={hovered === 0} onEnter={() => setHovered(0)} onLeave={() => setHovered(null)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {articles.slice(1).map((a, i) => (
              <NewsSmallCard key={a.title} {...a} readLabel={t.readMore} isMobile={true} hovered={hovered === i + 1} onEnter={() => setHovered(i + 1)} onLeave={() => setHovered(null)} />
            ))}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1.35fr 1fr", gap: 16, alignItems: "stretch" }}>
          <div style={{ minHeight: 460 }}>
            <NewsFeaturedCard {...articles[0]} readLabel={t.readArticle} hovered={hovered === 0} onEnter={() => setHovered(0)} onLeave={() => setHovered(null)} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {articles.slice(1).map((a, i) => (
              <NewsSmallCard key={a.title} {...a} readLabel={t.readMore} hovered={hovered === i + 1} onEnter={() => setHovered(i + 1)} onLeave={() => setHovered(null)} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Partners() {
  const lang = useLang();
  const logos = [IMAGES.partner1, IMAGES.partner2, IMAGES.partner3, IMAGES.partner4, IMAGES.partnerPJ, IMAGES.partner6];
  return (
    <div style={{ background: "#e9ecf0", padding: "32px 40px", borderTop: `1px solid ${COLORS.greyMid}` }}>
      <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.greyDark, letterSpacing: "0.18em", textTransform: "uppercase", textAlign: "center", marginBottom: 20 }}>
        {TRANSLATIONS[lang].partners}
      </div>
      <div style={{ display: "flex", gap: 40, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
        {logos.map((src, i) => (
          <img key={i} src={src} alt={`Partner ${i + 1}`} style={{ height: 36, objectFit: "contain", filter: "grayscale(1)", opacity: 0.5, transition: "opacity 0.2s, filter 0.2s", cursor: "pointer" }}
            onMouseEnter={e => { e.target.style.opacity = 1; e.target.style.filter = "grayscale(0)"; }}
            onMouseLeave={e => { e.target.style.opacity = 0.5; e.target.style.filter = "grayscale(1)"; }}
          />
        ))}
      </div>
    </div>
  );
}

function Contact() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].contact;
  const f = t.form;
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [form, setForm] = useState({ name: "", email: "", product: "", message: "" });
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const infoItems = [
    { icon: "📞", label: t.infoLabels.phone, val: "+86 138 2527 1417" },
    { icon: "✉️", label: t.infoLabels.email, val: "marketing@arcuchicnc.com" },
    { icon: "📍", label: t.infoLabels.address, val: "Block A, Building 2, Shangzhi Technology Park, No.380 Guangming Ave, Shenzhen, China" },
    { icon: "🕐", label: t.infoLabels.support, val: t.supportVal },
  ];

  return (
    <section id="contact" style={{
      background: COLORS.navyDark, padding: isMobile ? "48px 20px" : "72px 40px",
      display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: isMobile ? 36 : 56, alignItems: "start",
    }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 28, height: 2, background: COLORS.red }} />
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(255,150,150,0.85)", letterSpacing: "0.22em", textTransform: "uppercase" }}>{t.label}</span>
        </div>
        <h2 style={{ fontFamily: "Montserrat,sans-serif", fontSize: "clamp(22px,2.5vw,30px)", fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: "0.03em" }}>
          {t.title} <span style={{ color: COLORS.red }}>{t.accent}</span>
        </h2>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.8, margin: "14px 0 28px", fontWeight: 300 }}>{t.desc}</p>
        {infoItems.map(({ icon, label, val }) => (
          <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, background: COLORS.red, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 16 }}>{icon}</div>
            <div>
              <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.45)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{val}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: COLORS.white, padding: isMobile ? 24 : 36 }}>
        {[
          { label: f.nameLbl, name: "name", type: "text", placeholder: f.namePlh },
          { label: f.emailLbl, name: "email", type: "email", placeholder: f.emailPlh },
        ].map(({ label, name, type, placeholder }) => (
          <div key={name} style={{ marginBottom: 16 }}>
            <label style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.navy, letterSpacing: "0.12em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>{label}</label>
            <input type={type} name={name} placeholder={placeholder} value={form[name]} onChange={handleChange}
              style={{ width: "100%", background: COLORS.greyPage, border: `1px solid ${COLORS.greyMid}`, padding: "10px 14px", fontFamily: "Open Sans,sans-serif", fontSize: 13, color: COLORS.textDark, outline: "none" }}
            />
          </div>
        ))}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.navy, letterSpacing: "0.12em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>{f.productLbl}</label>
          <select name="product" value={form.product} onChange={handleChange}
            style={{ width: "100%", background: COLORS.greyPage, border: `1px solid ${COLORS.greyMid}`, padding: "10px 14px", fontFamily: "Open Sans,sans-serif", fontSize: 13, color: COLORS.textDark, outline: "none", appearance: "none" }}>
            <option value="">{f.productPlh}</option>
            {f.products.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.navy, letterSpacing: "0.12em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>{f.msgLbl}</label>
          <textarea name="message" value={form.message} onChange={handleChange} placeholder={f.msgPlh} rows={4}
            style={{ width: "100%", background: COLORS.greyPage, border: `1px solid ${COLORS.greyMid}`, padding: "10px 14px", fontFamily: "Open Sans,sans-serif", fontSize: 13, color: COLORS.textDark, outline: "none", resize: "vertical" }} />
        </div>
        <button style={{ width: "100%", background: COLORS.red, color: "#fff", fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: 14, border: "none", cursor: "pointer" }}>
          {f.sendBtn}
        </button>
      </div>
    </section>
  );
}

function Footer() {
  const lang = useLang();
  const t = TRANSLATIONS[lang].footer;
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <footer style={{ background: COLORS.navyDark, padding: isMobile ? "40px 20px 20px" : "48px 40px 24px" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr 1fr",
        gap: isMobile ? 28 : 40,
        marginBottom: 40,
      }}>
        <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
          <img src={IMAGES.logoFooter} alt="Arcuchi" style={{ height: 40, objectFit: "contain", marginBottom: 14, display: "block" }} />
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.8, maxWidth: 260, fontWeight: 300 }}>
            {t.desc}
          </p>
        </div>
        {t.cols.map(col => (
          <div key={col.title}>
            <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, paddingBottom: 8, borderBottom: `1px solid ${COLORS.red}` }}>
              {col.title}
            </div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {col.links.map(l => <li key={l}><a href="#" style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", textDecoration: "none" }}>{l}</a></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div style={{
        borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 20,
        display: "flex", flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between", alignItems: "center",
        gap: isMobile ? 14 : 0,
      }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: isMobile ? "center" : "left" }}>
          {t.copy}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {[["FB", "https://www.facebook.com/profile.php?id=100095303481787"], ["TW", "https://twitter.com/arcuchi_cnc_10"], ["IG", "https://www.instagram.com/arcuchicnc/"], ["LI", "https://www.linkedin.com/in/arcuchi-cnc-50a672287/"]].map(([label, href]) => (
            <a key={label} href={href} target="_blank" rel="noreferrer"
              style={{ width: 30, height: 30, background: "rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, fontSize: 10, fontFamily: "Montserrat,sans-serif", fontWeight: 700, color: "rgba(255,255,255,0.45)", textDecoration: "none" }}>
              {label}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────

function useHash() {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return hash;
}

function HomePage() {
  const [activeSection, setActiveSection] = useState("home");
  const [lang, setLang] = useState("en");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "solutions", "about", "services", "news", "contact"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) { setActiveSection(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <LangContext.Provider value={lang}>
      <style>{fonts}</style>
      <TopBar />
      <Navbar activeSection={activeSection} lang={lang} setLang={setLang} />
      <Hero />
      <StatsBar />
      <Marquee />
      <Solutions />
      <About />
      <Services />
      <Testimonials />
      <News />
      <Partners />
      <Contact />
      <Footer />
    </LangContext.Provider>
  );
}

export default function ArcuchiWebsite() {
  const hash = useHash();
  if (hash === "#/about") return <AboutPage />;
  if (hash === "#/contact") return <ContactPage />;
  if (hash === "#/news") return <NewsPage />;
  if (hash.startsWith("#/news/")) return <NewsDetailPage slug={hash.slice(7)} />;
  if (hash === "#/products") return <ProductsPage />;
  return <HomePage />;
}
